// QC6 / QC7 acceptance forms (docs/modules/workflow-qc.md).
//
// Two banks, one per form, each laid out the way the firm's Word forms are:
//   Form QC6(4) — New engagement / client acceptance checklist
//   Form QC7(4) — Continuing engagement / client evaluation checklist
//
// Each form has three BLOCKS, rendered by components/workflow/QcForm.js:
//   1. Client & Engagement Particulars — the two-column table at the top of the
//      Word form (fees, prior-year facts, PIE / TNE / TNA …).
//   2. Objectives and detailed tests — the procedures table, grouped under the
//      Word form's "Objective – …" headings. Every procedure row has a
//      Comment; rows that are themselves a question also carry a Yes/No.
//   3. Conclusion — risk rating, EQR, decision, STR, declaration.
// The Word form's explanatory notes ("Consider: …", the AML beneficial-owner
// definition, the TCWG fee-disclosure note …) are kept in full as `tip`s —
// shown on hover, never dropped.
//
// Every question is ALWAYS rendered. A question that does not apply (the
// follow-up to a "No", a procedure step a branch skipped, the section the
// engagement type does not use) is not hidden: it is auto-filled with its
// `na` value and locked, so the printed form reads complete. `applyAutoAnswers`
// keeps those values in step with the answers they depend on.
//
// Answers are one flat JSON object keyed by logical field id. A 'money' item
// occupies `<id>` (amount) and `<id>_ccy` (currency); a 'procedure' row
// occupies `<id>_yn` (when it asks Yes/No) and `<id>_comment`. A money item
// marked `split` (the Estimated fees) also occupies `<id>_split` when the form
// covers more than one engagement: `{ <engagementId>: amount }` in the Total's
// currency. Once any engagement has an amount, every covered engagement needs
// one and `<id>` (the Total) is their sum; with no amounts `<id>` is the Total
// the requester types. A date item marked
// `split` (the Expected FS Signing Date) works the same way: with no split
// `<id>` is the date of every covered engagement; `<id>_split` =
// `{ <engagementId>: 'YYYY-MM-DD' }` gives every covered engagement its own
// date (all or none) and `<id>` is then the earliest of them.
// `answerableQuestions()` returns that flattened list — validation, read-only
// views, History diffs and reviewer comments all key on it.
//
// What is NOT asked here (it comes from the covered engagement and is shown in
// the form header, read-only): CLIENT / PROSPECTIVE CLIENT, PERIOD ENDED /
// ENDING, Type of engagement. The other way round, TNE / TNA (Particulars)
// and the EQR (Conclusion) are answered HERE and written to every covered
// engagement on approval (server/services/qcAnswers.js).
//
// Item shape:
//   { id, label, type, tip?, required?, options?, na?, placeholder?, split? }
//   tip: string | string[] | (answers) => string | string[] — a function tip
//        reads the current answers (the EQR row explains its own rating)
//   type: 'money' | 'percent' | 'number' | 'text' | 'textarea' | 'yesno'
//       | 'radio' (options[]) | 'select' (options[]) | 'eqr' (eligible EQR
//         dropdown, GET /api/workflow/qc/eqr-options) | 'checkbox'
//       | 'procedure' ({ no, yesno?: true, naScope?: 'comment' })
//       | 'note' (static text, no answer)
//   required: boolean | (answers) => boolean
//   na: { when: (answers) => boolean, value: string }
//     | [{ when, value }, …]  — first match wins
//     For a procedure the value lands in `<id>_comment` (and yn/date clear);
//     naScope 'comment' locks only the comment, the row stays signable.

const { engagementTag, engagementDisplayName } = require('../utils/engagementName');

const CURRENCIES = ['SGD', 'USD', 'EUR', 'GBP', 'CNY', 'HKD', 'MYR', 'AUD', 'JPY'];
const DEFAULT_CURRENCY = 'SGD';
const RISK_OPTIONS = ['Low', 'Moderate', 'High'];
const PIE_TYPE_OTHERS = 'Others';
const PIE_TYPE_OPTIONS = ['Publicly traded entity', 'Financial Institution', 'Large charity', 'Large Institution of Public Character (IPC)', PIE_TYPE_OTHERS];
const EQR_NOT_REQUIRED = 'NOT REQUIRED';
const INDEP_TYPE_OPTIONS = ['Audit / Review engagement', 'Non-assurance engagement'];
// Non-assurance follow-up: which kind of service. Decides who reviews the
// form (qcReviewerPlanOf) — routine / non-routine services are reviewed by a
// Director first, FAS by Partners only, the same as an audit.
const NAS_TYPE_OPTIONS = ['Routine services', 'Non-routine services', 'FAS services'];

// ── N/A values (constants so applyAutoAnswers can recognise its own writes) ──
const NA = 'Not applicable';
const NA_NOT_PIE = 'Not applicable (not a PIE)';
const NA_NO_NAS = 'Not applicable (no NAS fees)';
const NA_NO_PEP = 'Not applicable (no PEP identified)';
const NA_RECOVERY = 'Not required (recovery rate is 30% or more)';
const NA_AUDIT_SECTION = 'Not applicable (non-assurance engagement)';
const NA_NAS_SECTION = 'Not applicable (audit / review engagement)';
const NA_LISTED = 'Not applicable (the client is listed)';
const NA_CHOOSE_TYPE = 'Choose the engagement type above';
const NA_CHOOSE_RATING = 'Choose the risk rating above';
const SKIP_A = 'Skipped — see steps a(i) and a(ii)';
// The same skip as stored on forms filled in before the steps were numbered a(i) / a(ii).
const SKIP_A_OLD = 'Skipped — see step (a)';
const SKIP_AB = 'Skipped — see steps (a) and (b)';
const SKIP_D = 'Skipped — see step (d)';
const SKIP_F = 'Skipped — no threats identified in steps (c), (d) or (e)';
const SKIP_F_NAS = 'Skipped — no services identified in steps (b) or (c)';

const yes = (v) => v === 'Yes';
const no = (v) => v === 'No';
const blank = (v) => v === undefined || v === null || String(v).trim() === '';
const recoveryOk = (v) => !blank(v) && Number.isFinite(Number(v)) && Number(v) >= 30;
const recoveryLow = (v) => !blank(v) && Number.isFinite(Number(v)) && Number(v) < 30;

// ── Notes shared by both forms (verbatim from the Word forms) ────────────────
const TIP_NAS = [
  'Enquire with Finance / NAS Departments.',
  'Note — Transparency of Information Regarding Fees for Audit Clients that are Public Interest Entities: communication of fee-related information (for both audit and NAS) with those charged with governance (TCWG) to assist their assessment of the firm\'s independence prior to acceptance of the engagement.',
  'For new NAS provided to Audit Clients that are PIE, include the communication with TCWG as an attachment.',
];
const TIP_FEE_CONCENTRATION = [
  'Non-audit services fees / audit fees = x%.',
  'Audit Clients that are Publicly Traded Entities, Listed Business Trusts (BT) and Listed Real Estate Investment Trusts (REIT): where the amount of annual NAS fees by the firm or its network firms compared to total annual audit fees (excluding Audit-Related Services fees) is 50% or more, the firm shall disclose to TCWG and discuss the safeguards to be applied to reduce the threat to an acceptable level.',
  'Computed as % of NAS from the client and its controlled entities (excluding ARS fees) over fees from the audit of the financial statements of the client and its controlled entities.',
];
const TIP_RECOVERY = [
  'Attach the budget or staff booking form.',
  'Should be at least 30%. Provide reasons for acceptance if the budgeted fee recovery percentage is below 30%.',
];
const TIP_PIE_TYPE = 'Publicly traded entity, Financial Institution, large charity, large Institution of Public Character (IPC), or another type (specify it below).';
const TIP_INTEGRITY_CONSIDER = [
  'Consider:',
  '• discussion with the party who introduced the client;',
  '• knowledge gained through work done by other departments within the firm;',
  '• media and any other reports;',
  '• client\'s relationship with any regulatory authority; and',
  '• client\'s attitude to fairness in financial reporting.',
];
const TIP_INDEPENDENCE_CONSIDER = [
  'Consider:',
  '• Undue dependence',
  '• Proposed contingent fees',
  '• Preparation of accounting records',
  '• Receipt of hospitality, goods, or services',
  '• Voting on audit appointments',
  '• Director or senior employee joining client',
  '• Participation in affairs of client',
  '• Influences from the outside practice (including associated firms)',
  '• Litigation (actual or threatened)',
  '• Family or professional relationships',
  '• Prohibited services',
  '• Financial involvement, including trustee investments, mutual business interests, loans, beneficial interests — shares / trusts, overdue fees',
  '• Network firm independence conflict checking procedures performed (i.e. conflict of interest to be considered by completing either "Audit and Review Engagements – Firm and Network Independence" or "Non-assurance Engagements – Firm and Network Independence", which are part of this form).',
];
const TIP_AML = (formName) => [
  '1. Perform a search of the company\'s name, all directors and ultimate beneficial owners owning more than 25% equity interest in the ultimate holding company using LexisNexis. This screening covers not only identifying any PEPs but also terrorist names, terrorist organisations and names sanctioned by United States / United Nations. A copy of the screening should be attached to ' + formName + '.',
  '2. Carry out a Google check on the company\'s name, the names of the directors and ultimate BOs owning more than 25% equity interest in the ultimate holding company. A copy of the Google check should be attached to ' + formName + '.',
  '3. For audit clients whereby the companies are incorporated by our related entities, PKF-CAP Corporate Services Pte Ltd ("CS") or PKF-Khoo Management Services Pte Ltd ("KMS"), carry out a review of the screening and Google checking that have been carried out by CS or KMS. If the result is satisfactory, document the review that you have performed. No further screening or checking is required unless the work performed is considered not adequate for our purposes. This placing of reliance is acceptable so long as the screening carried out by CS or KMS is current within 12 months from our client / engagement evaluation date.',
  'Note — An accounting entity or individual practitioner need not inquire if there exists any beneficial owner in relation to a client where the client is: (a) an entity listed on the Singapore Exchange; (b) an entity listed on a stock exchange outside Singapore which is regulated by an authority of a country or territory other than Singapore regulating the provision of financial services; (c) a Singapore financial institution; (d) a financial institution incorporated or established outside Singapore that is subject to and supervised for compliance with requirements for the prevention of money laundering and the financing of terrorism consistent with the standards set by the FATF; or (e) an investment vehicle, the managers of which are (i) Singapore financial institutions, or (ii) financial institutions incorporated or established outside Singapore and subject to and supervised for compliance with requirements for the prevention of money laundering and the financing of terrorism consistent with the standards set by the FATF — unless the accounting entity or individual practitioner has doubts about the veracity of the information obtained in carrying out CDD measures under these Rules or suspects that the client is carrying out or facilitating money laundering or the financing of terrorism.',
  '"Beneficial owner" means: (a) an individual who ultimately owns all of the assets or undertakings of the client (whether or not the client is a body corporate); (b) an individual who has ultimate control or ultimate effective control over, or has executive authority in, the client; or (c) an individual on whose behalf the client has employed or engaged the services of an accounting entity.',
  'Suggested wording: "We have performed LexisNexis screening and Google searches of the Company [Appendix _], ultimate beneficial owner(s) [Appendix _] and the directors of the Company [Appendix _]." Document the results of the screenings and searches.',
];
const TIP_PEP = 'Note — For domestic PEPs in Singapore and PEPs where their wealth is known publicly, there is no need to establish their source of funds for their operations.';
const TIP_RISK_CATEGORIES = [
  'Appendix 1 — extract from the Firm\'s Quality Management Manual (section 8.14). The Firm classifies its engagements into three risk categories:',
  '• High — high risk engagement requiring full scope engagement quality review by the EQR.',
  '• Moderate — moderate risk engagement may require specific scope engagement quality reviews or subject to EP\'s professional judgement.',
  '• Low — low risk engagement requiring no engagement quality reviews.',
  'An engagement quality review is an objective evaluation of the significant judgements made by the engagement team and the conclusions reached in formulating the report before the final report is issued. A "high risk" client (rated "H") is a client considered to be of high public interest or likely to be subject to higher levels of scrutiny.',
  'High risk engagements (full engagement quality review): 1. Transnational audit (TNA) — only necessary for a subsidiary that is full scope / material / significant to the group; 2. Publicly traded or listed companies; 3. Large charities or large IPCs with donation income of S$10 million or more; 4. Other PIEs such as banks, financial institutions, insurance entities, large pension schemes and regulated fund management activities, including if their financial statements are likely to be used internationally or if they are important National Institutions; 5. "For sale" entities or those known to be targets for acquisition; 6. Entities with particular reporting difficulties where there may be doubts over the nature of the audit report to be issued; 7. Entities with significant risk indicators such as suspected fraud, regulatory investigations, significant non-compliance with laws and regulations, or other matters that may attract adverse public or regulatory attention; 8. Entities involved in cryptocurrency / Digital Payment Token (DPT), and commodity trading companies engaged in speculative (paper) trading or relying heavily on trade financing; 9. Clients with other significant concerns identified by the EP that require a full engagement quality review.',
  'Moderate risk engagements (rated "M"; specific scope review or EP\'s judgement): 1. Clients with recent adverse news; 2. Clients with an emphasis of matter (EOM) in the auditor\'s report; 3. Clients with non-compliance with laws and regulations (NOCLAR); 4. First-year audit of a new client arising from a change of auditors (unless the engagement is non-complex and straightforward and, on the EP\'s recommendation, does not need an engagement quality review); 5. Clients with material uncertainty related to going concern; 6. Entities where a modified audit report was issued in the last financial statements, or where one is expected in the current financial statements; 7. Clients with other significant concerns identified by the EP that require a specific engagement quality review.',
];
// The EQR row's note — only the reason the rating gives, per the owner.
const tipEqrForRating = (rating) => {
  if (rating === RISK_OPTIONS[0]) return 'The client risk is rated Low; you may still name a reviewer.';
  if (RISK_OPTIONS.includes(rating)) return `Required because the client risk is rated ${rating} — an engagement quality reviewer must be assigned.`;
  return 'Required when the client risk is rated Moderate or High.';
};
const TIP_STR = 'If yes, set out the rationale and basis for the STR. If in doubt, seek advice from legal counsel after discussing with the Managing Partner and / or the Quality Management Partner.';
const TIP_FIRM_NETWORK_HEADER = 'Confirm that no conflict exists. If this section is not applicable, it is marked "Not applicable" automatically according to the engagement type chosen above.';
const TIP_NAS_TYPE = 'Decides who reviews this form: routine and non-routine services are reviewed by a Director first and then a Partner (a PIE client adds a further Partner); FAS services are always reviewed by two Partners.';

const DECLARATION = {
  id: 'q_declaration', type: 'checkbox', required: true,
  label: 'I hereby declare that the information provided is correct and accurate.',
};

// ── Small builders ───────────────────────────────────────────────────────────
// Object.assign, not `{ ...x }`: under the production browserslist babel
// rewrites object spread with an ESM helper import, which would turn this
// CommonJS file into an ES module and break every `module.exports` consumer.
const followUp = (id, label, type, whenId, extra = {}) => Object.assign({
  id, label, type, required: true, na: { when: (a) => !yes(a[whenId]), value: NA },
}, extra);
const proc = (id, no, label, extra = {}) => Object.assign({ id, no, label, type: 'procedure', required: true }, extra);
// TNE / TNA: the answer given here is written to every covered engagement on
// approval, and each needs its basis. A registration that asked for QM
// screening answered the same pair — the New form starts from it
// (qcPrefillGroups), still editable.
const TIP_TNE_TNA = 'On approval this answer is written to every covered engagement. To change it later, file a QC Amendment.';
// The questions the rest of the system acts on carry the SAME wording on both
// forms (a QC6 and a QC7 answer land in the same engagement fields).
const FEE_LABEL = 'Estimated fees';
const RATING_LABEL = 'Client risk rated in current year';
// `split` on a choice / text item: a form covering several engagements may
// answer it per engagement (splitDerivedValue says what the one answer then
// becomes). Type of PIE follows PIE engagement by engagement (`splitFollows`).
const pieTneTna = (prefix) => [
  { id: `${prefix}_pie`, label: 'Public Interest Entity (PIE)?', type: 'yesno', required: true, split: true },
  { id: `${prefix}_pie_type`, label: 'Type of PIE', type: 'select', options: PIE_TYPE_OPTIONS, required: true, tip: TIP_PIE_TYPE,
    other: { value: PIE_TYPE_OTHERS, label: 'Type of PIE (if Others)' },
    split: true, splitFollows: { id: `${prefix}_pie`, value: NA_NOT_PIE },
    na: { when: (a) => !yes(a[`${prefix}_pie`]), value: NA_NOT_PIE } },
  { id: `${prefix}_tne`, label: 'Is this a transnational entity (TNE)?', type: 'yesno', required: true, tip: TIP_TNE_TNA },
  { id: `${prefix}_tne_basis`, label: 'Basis for the TNE assessment', type: 'textarea', required: true },
  { id: `${prefix}_tna`, label: 'Is this a transnational audit (TNA)?', type: 'yesno', required: true, tip: TIP_TNE_TNA },
  // `reference`: the editing form prints the TNA / TNE assessment criteria
  // (the same View Reference as the screening questions) right below the
  // table of the block holding this row, outside the table.
  { id: `${prefix}_tna_basis`, label: 'Basis for the TNA assessment', type: 'textarea', required: true, reference: 'tnaTne' },
];
// ── Prefill from the covered engagements ────────────────────────────────────
// The New form (Workflow / Management, the legacy-import form included) starts
// from what the engagements already say — PIE / Type of PIE and the Estimated
// fees (the type of activities is typed in, owner 2026-10-15) — and, when the
// Registration asked for QM screening, TNE / TNA with their bases
// (`screeningAnswers` on the picker feed; the first engagement with answers
// wins). Each group is written in only while its own answers (`ids`) are all
// blank, stays editable, and the note above the form names the groups still
// untouched (components/workflow/QcPrefill.js). Several covered engagements:
// a group that differs per engagement is written per engagement — the same
// shape "Set per Engagement" produces; an engagement without the answer
// leaves the group out. `legacy`: the signed-form record asks PIE and the fees only.
const qcPrefillGroups = (engagements, qcType, { legacy = false } = {}) => {
  const list = (engagements || []).filter(Boolean);
  if (!qcType || list.length === 0) return [];
  const p = String(qcType).toUpperCase() === 'QC7' ? 'q7' : 'q6';
  const ids = list.map((e) => String(coveredEngagementIdOf(e)));
  const same = (vals) => vals.every((v) => v === vals[0]);
  const groups = [];
  // PIE / Type of PIE (+ its Others text)
  const pies = list.map((e) => (e.isPie === true ? 'Yes' : e.isPie === false ? 'No' : null));
  if (pies.every((v) => v !== null)) {
    const types = list.map((e) => (e.isPie ? String(e.pieType || '') : NA_NOT_PIE));
    const others = list.map((e) => (e.isPie && e.pieType === PIE_TYPE_OTHERS ? String(e.pieTypeOther || '') : ''));
    const answers = {};
    if (list.length === 1 || (same(pies) && same(types) && same(others))) {
      answers[`${p}_pie`] = pies[0];
      if (pies[0] === 'Yes' && types[0]) answers[`${p}_pie_type`] = types[0];
      if (others[0]) answers[`${p}_pie_type_other`] = others[0];
    } else {
      answers[`${p}_pie_split`] = Object.fromEntries(ids.map((id, i) => [id, pies[i]]));
      answers[`${p}_pie_type_split`] = Object.fromEntries(ids.map((id, i) => [id, types[i]]));
      const specs = Object.fromEntries(ids.map((id, i) => [id, others[i]]).filter(([, v]) => v));
      if (Object.keys(specs).length > 0) answers[`${p}_pie_type_other_split`] = specs;
    }
    groups.push({ key: 'pie', label: 'PIE', ids: [`${p}_pie`, `${p}_pie_split`], answers });
  }
  // Estimated fees — every engagement's own amount, one currency (a shared
  // registration Total is not an engagement's own figure).
  const feeId = feeItemIdOf(qcType);
  const fees = list.map((e) => (e.proposedFees === null || e.proposedFees === undefined || e.proposedFeesShared ? null : Number(e.proposedFees)));
  const ccys = [...new Set(list.map((e) => e.proposedFeesCcy || DEFAULT_CURRENCY))];
  if (fees.every((v) => v !== null && Number.isFinite(v)) && ccys.length === 1) {
    const answers = { [`${feeId}_ccy`]: ccys[0] };
    if (list.length === 1) answers[feeId] = String(fees[0]);
    else answers[`${feeId}_split`] = Object.fromEntries(ids.map((id, i) => [id, String(fees[i])]));
    groups.push({ key: 'fee', label: 'Estimated fees', ids: [feeId, `${feeId}_split`], answers });
  }
  if (legacy) return groups;
  // TNE / TNA from the Registration's screening questions
  const src = list.find((e) => e && e.screeningAnswers && typeof e.screeningAnswers.isTne === 'boolean');
  if (src) {
    const s = src.screeningAnswers;
    const yn = (v) => (v === true ? 'Yes' : v === false ? 'No' : undefined);
    const answers = {};
    if (yn(s.isTne) !== undefined) answers[`${p}_tne`] = yn(s.isTne);
    if (s.tneBasis) answers[`${p}_tne_basis`] = s.tneBasis;
    if (yn(s.isTna) !== undefined) answers[`${p}_tna`] = yn(s.isTna);
    if (s.tnaBasis) answers[`${p}_tna_basis`] = s.tnaBasis;
    groups.push({
      key: 'screening', label: 'TNE / TNA', ids: [`${p}_tne`, `${p}_tne_basis`, `${p}_tna`, `${p}_tna_basis`],
      answers, requestNo: s.requestNo || null, engagement: src,
    });
  }
  return groups;
};

/** Did the Estimated fees answer (amount, currency or per-engagement amounts) change between two answer sets? */
const feeAnswersChanged = (before, after, qcType) => {
  const id = feeItemIdOf(qcType);
  const b = before || {};
  const a = after || {};
  const norm = (x) => (blank(x) ? '' : String(x).trim());
  return norm(b[id]) !== norm(a[id]) || norm(b[`${id}_ccy`]) !== norm(a[`${id}_ccy`])
    || JSON.stringify(splitOf(b, `${id}_split`)) !== JSON.stringify(splitOf(a, `${id}_split`));
};

const conclusion = (p, { decisionLabel, decisionOptions, strLabel, notes }) => ({
  id: 'conclusion',
  title: 'Conclusion',
  sections: [{
    id: 'conclusion_main',
    items: [
      { id: `${p}_c_risks`, label: p === 'q6' ? 'Are there any significant risks associated with the acceptance of the engagement?' : 'Based on the above information, are there any risks associated with the proposed work?', type: 'yesno', required: true, tip: 'Are there any safeguards that should be applied? If yes, specify the nature of the safeguard to address the issue(s) identified in the evaluation.' },
      followUp(`${p}_c_risks_details`, 'Risks identified and the safeguards to be applied', 'textarea', `${p}_c_risks`),
      // Per engagement on a multi-engagement form; the form's rating is then
      // the highest of them (so the EQR below follows the riskiest one).
      { id: `${p}_c_rating`, label: RATING_LABEL, type: 'radio', options: RISK_OPTIONS, required: true, tip: TIP_RISK_CATEGORIES, split: true, splitPick: 'highest' },
      { id: `${p}_c_rating_reason`, label: 'Reason for the risk rating', type: 'textarea', required: true, tip: 'Document further details or elaboration.', split: true },
      // The EQR follows the risk rating: Moderate / High ⇒ one must be named;
      // Low ⇒ optional (still selectable, may stay blank). The note says which
      // rating put it there — nothing else.
      { id: `${p}_c_eqr`, label: 'Name of engagement quality reviewer assigned by Head of Division / Quality Management Partner', type: 'eqr',
        required: (a) => a[`${p}_c_rating`] === RISK_OPTIONS[1] || a[`${p}_c_rating`] === RISK_OPTIONS[2],
        tip: (a) => tipEqrForRating(a[`${p}_c_rating`]),
        na: { when: (a) => blank(a[`${p}_c_rating`]), value: NA_CHOOSE_RATING } },
      { id: `${p}_c_decision`, label: decisionLabel, type: 'radio', options: decisionOptions, required: true },
      { id: `${p}_c_str`, label: strLabel, type: 'yesno', required: true, tip: TIP_STR },
      followUp(`${p}_c_str_rationale`, 'Rationale and basis for the STR', 'textarea', `${p}_c_str`),
      DECLARATION,
      // Last field of the form: the reminder date until the Signed Financial Statements is
      // approved. Written to every covered engagement on approval; corrections
      // go through a QC Amendment (same as the EQR / TNA / TNE above).
      // A non-assurance engagement files no Signed Financial Statements — the
      // field is "Not applicable" and the form's approval completes the
      // engagement (qcIsNonAssuranceOf).
      {
        id: `${p}_c_expected_fs_date`, label: 'Expected Financial Statements Signing Date',
        type: 'date', required: true, split: true,
        tip: 'The date the financial statements are expected to be signed. If the engagements covered have different dates, set a date per engagement.',
        na: { when: (a) => a[`${p}_indep_type`] === INDEP_TYPE_OPTIONS[1], value: NA_AUDIT_SECTION },
      },
      ...(notes ? [{ id: `${p}_notes`, type: 'note', label: 'Notes', text: notes }] : []),
    ],
  }],
});

// Branching helpers for the Firm & Network Independence procedures.
const isAudit = (a, id) => a[id] === INDEP_TYPE_OPTIONS[0];
const isNas = (a, id) => a[id] === INDEP_TYPE_OPTIONS[1];
// Until the engagement type is answered, BOTH sets wait (a "non-assurance"
// label on an audit form before the question is answered would mislead).
const auditSection = (typeId) => [
  { when: (a) => blank(a[typeId]), value: NA_CHOOSE_TYPE },
  { when: (a) => !isAudit(a, typeId), value: NA_AUDIT_SECTION },
];
const nasSection = (typeId) => [
  { when: (a) => blank(a[typeId]), value: NA_CHOOSE_TYPE },
  { when: (a) => !isNas(a, typeId), value: NA_NAS_SECTION },
];

// ══════════════════════════════════════════════════════════════════════════════
// QC6 — New engagement / client acceptance checklist
// ══════════════════════════════════════════════════════════════════════════════
const QC6_FORM = {
  qcType: 'QC6',
  formNo: 'FORM QC6(4)',
  title: 'New engagement / client acceptance checklist',
  tableTitle: 'NEW ENGAGEMENT OR CLIENT ACCEPTANCE CHECKLIST',
  clientLabel: 'PROSPECTIVE CLIENT',
  periodLabel: 'PERIOD ENDING',
  blocks: [
    {
      id: 'particulars',
      title: 'Client & Engagement Particulars',
      sections: [{
        id: 'particulars_main',
        items: [
          { id: 'q6_pkf_entity', label: 'Name of PKF entity proposing for the service', type: 'text', required: true },
          { id: 'q6_referral', label: 'Source of referral of prospective client', type: 'text', required: true },
          { id: 'q6_est_fee', label: FEE_LABEL, type: 'money', required: true, split: true },
          { id: 'q6_budget_recovery', label: 'Budgeted fee recovery rate', type: 'percent', required: true, tip: ['Budgeted fee / budgeted time cost.', ...TIP_RECOVERY] },
          { id: 'q6_recovery_reason', label: 'Reasons for acceptance (budgeted fee recovery below 30%)', type: 'textarea', required: (a) => recoveryLow(a.q6_budget_recovery), na: { when: (a) => recoveryOk(a.q6_budget_recovery), value: NA_RECOVERY } },
          { id: 'q6_has_nas', label: 'Non audit fee from tax services, accounting, valuation, secretarial services and other non-audit services (NAS) (if any)?', type: 'yesno', required: true, tip: TIP_NAS },
          followUp('q6_nas_service_types', 'Non-audit services (NAS) type(s)', 'text', 'q6_has_nas', { na: { when: (a) => !yes(a.q6_has_nas), value: NA_NO_NAS } }),
          followUp('q6_nas_fee', 'Non-audit services (NAS) fee', 'money', 'q6_has_nas', { na: { when: (a) => !yes(a.q6_has_nas), value: NA_NO_NAS } }),
          { id: 'q6_unpaid', label: 'Any outstanding unpaid fees?', type: 'yesno', required: true, tip: 'Check to AR aging listing or enquire with the finance department.' },
          followUp('q6_unpaid_amount', 'Outstanding fees — amount', 'money', 'q6_unpaid'),
          followUp('q6_unpaid_services', 'Outstanding fees — service type(s)', 'text', 'q6_unpaid'),
          { id: 'q6_fee_concentration', label: 'Fee concentration', type: 'percent', required: true, tip: TIP_FEE_CONCENTRATION, na: { when: (a) => !yes(a.q6_has_nas), value: NA_NO_NAS } },
          { id: 'q6_conflict_check', label: 'Conflicts check done?', type: 'yesno', required: true, tip: ['Conflict check should be done at least 3 working days prior to the approval of the QC6(4).', 'The conflict check email is filed as an Appendix.'] },
          { id: 'q6_activities', label: 'Type of activities of prospective client', type: 'textarea', required: true },
          { id: 'q6_predecessor', label: 'Predecessor Auditor (if applicable)', type: 'text' },
          { id: 'q6_prior_opinion', label: 'Prior year\'s audit opinion', type: 'text' },
          { id: 'q6_predecessor_reason', label: 'Reasons for discontinuation of Predecessor Auditor', type: 'textarea' },
          ...pieTneTna('q6'),
        ],
      }],
    },
    {
      id: 'procedures',
      title: 'Objectives and detailed tests',
      sections: [
        {
          id: 'p6_s1', title: 'Objective – To ensure that the work for the client does not involve unacceptable risks',
          items: [
            proc('p6_1', 1, 'Decide whether we can rely on the management\'s integrity and whether association with the client may damage the firm\'s reputation.', {
              tip: [...TIP_INTEGRITY_CONSIDER,
                'Suggested wording — "We have carried out the following procedures for our evaluation: we have discussed with [Name] who introduced the client; we held a preliminary meeting with the client [Client name and position] to obtain an understanding of the client\'s business activities, discussed management\'s expectations and the reasons for appointing our firm, and considered management\'s responses in assessing their integrity, competence and attitude towards fair financial reporting; we have obtained and reviewed Questnet (Bizfile) profile searches [Appendix _]; we have screened in Google search the names of the Company, ultimate beneficial owner and directors [Appendix _]; we have screened in LexisNexis the names of the Company, ultimate beneficial owner and directors [Appendix _]; we did not identify any conflict of interest in (TREND) (PKFI) for conflict search [Appendix _]; we have sent out a conflict check email dated DDMMYYYY within PKF Singapore [Appendix _]; we are not aware of any significant matters that would give rise to concerns over the integrity of management or to reputation issues by our association with the Company."'],
            }),
            proc('p6_2', 2, 'If we have doubts resulting from the considerations above, decide whether the potential risks can be mitigated by introducing additional precautions.', {
              tip: ['Such as: additional procedures, especially independent third-party confirmations; or additional review by an engagement quality reviewer (i.e. safeguard reviewer).',
                'Suggested wording — "From the above information, we have not noted any indicators that would cause concern for us in taking up this audit engagement."'],
            }),
          ],
        },
        {
          id: 'p6_s2', title: 'Objective – To ensure that the firm can comply with relevant ethical requirements',
          items: [
            proc('p6_3', 3, 'Consider whether the firm can comply with relevant ethical requirements, including the firm\'s independence. Include in your consideration potential independence or conflicts of interest problems arising from relationships with other clients, firm members, or their immediate families; providing non-assurance services; or unpaid fees.'),
            proc('p6_4', 4, 'Regarding the proposed relationship between the firm and the client or any associated companies, decide whether we are, and can be seen to be, independent of the client.', {
              tip: [...TIP_INDEPENDENCE_CONSIDER,
                'Suggested wording — "We have carried out the following procedures: we did not identify any conflict of interest in TREND (PKFI) for conflict search [Appendix _]; we have not noted any threats to independence and / or conflicts of interest with the Companies; we are not aware of any independence or conflict of interest that requires additional precautions. We conclude that we are in compliance with the relevant ethical requirements, independence."'],
            }),
          ],
        },
        {
          id: 'p6_s3', title: 'Objective – To ensure that the firm has the ability to perform the professional services properly',
          items: [
            proc('p6_5', 5, 'Decide whether we have the competence to carry out the professional services properly. Consider whether special skills are needed to deal with the particular features or specialised reporting requirements of the client.', {
              tip: 'Suggested wording — "We have the necessary competency to carry out the engagement. Specialists involved: [e.g. FAS / IT]."',
            }),
            proc('p6_6', 6, 'Is there any reason to believe that management will not provide access to all information of which management is aware that is relevant to the preparation of the financial statements, including access to information relevant to disclosures?', {
              yesno: true, tip: 'Suggested wording — "We are not aware of any reason to believe that management will not provide access to all information."',
            }),
            proc('p6_7', 7, 'Confirm that the firm will have adequate resources to be able to do the work properly at the time the client needs it.', {
              tip: ['Note — for example, indicate if the engagement will be performed by outsourced staff.', 'Suggested wording — "We have the necessary resources to carry out the engagement."'],
            }),
            proc('p6_8', 8, 'Decide whether the level of fees agreed will be adequate to allow us to carry out the work deemed necessary, properly and with integrity.'),
          ],
        },
        {
          id: 'p6_s4', title: 'Objective – To ensure that our appointment is properly effected and that the scope of our work is acknowledged by the client',
          items: [
            proc('p6_9', 9, 'Send, and obtain a response to, a professional enquiry letter to the previous auditors / reporting accountants (if any).', {
              tip: 'A professional clearance letter from the predecessor auditor must be obtained prior to approval of QC6.',
            }),
            proc('p6_10', 10, 'Obtain and review the previous auditor\'s letter of resignation (if applicable) and statement of circumstances connected with their leaving office.'),
            proc('p6_11', 11, 'Obtain a copy of the DRIW / minutes appointing PKF as auditors.'),
            proc('p6_12', 12, 'Send an engagement letter and obtain the client\'s agreement to its terms before starting work.', {
              tip: 'Suggested wording — "The engagement letter will be prepared and submitted to management for approval upon approval of QC6(4) and the issuance of consent to act."',
            }),
          ],
        },
        {
          id: 'p6_s5', title: 'Objective – To establish the identity of the client to assist in identifying possible instances of money laundering ("AML") and terrorism financing activities ("CFT")',
          items: [
            proc('p6_13', 13, 'For anti-money laundering ("AML") and counter-terrorism financing ("CTF") activities, carry out the screening procedures and document the results.', { tip: TIP_AML('QC6(4)') }),
            proc('p6_14', 14, 'Are the directors identified to be a Politically Exposed Person ("PEP")? If yes, establish where the source of funds is obtained.', {
              yesno: true, naScope: 'comment', tip: TIP_PEP, na: { when: (a) => no(a.p6_14_yn), value: NA_NO_PEP },
            }),
            proc('p6_15', 15, 'Are the ultimate beneficial owners (individuals owning more than 25% equity interest in the ultimate holding company) a PEP? If yes, establish where the source of funds is obtained.', {
              yesno: true, naScope: 'comment', tip: TIP_PEP, na: { when: (a) => no(a.p6_15_yn), value: NA_NO_PEP },
            }),
          ],
        },
        {
          id: 'p6_s6', title: 'Firm and Network Independence – Acceptance Procedures',
          tip: TIP_FIRM_NETWORK_HEADER,
          items: [
            { id: 'q6_indep_type', label: 'This engagement is', type: 'radio', options: INDEP_TYPE_OPTIONS, required: true, tip: 'Audit and review engagements complete the first set of steps (a)–(f); non-assurance engagements complete the second set (a)–(d). The set that does not apply is marked "Not applicable".' },
            { id: 'q6_nas_type', label: 'Type of non-assurance service', type: 'radio', options: NAS_TYPE_OPTIONS, required: true, tip: TIP_NAS_TYPE, na: nasSection('q6_indep_type') },
          ],
        },
        {
          id: 'p6_s6a', title: 'Audit and Review Engagements only – Firm and Network Independence', subtitle: 'When proposing for an audit or review engagement:',
          items: [
            proc('p6_a1', 'a(i)', 'Is the potential client listed (public)?', {
              yesno: true, tip: 'If yes, proceed to (c). If not, research the family tree and ascertain if there is a listed (or publicly traded) entity or entities in the group.',
              na: auditSection('q6_indep_type'),
            }),
            proc('p6_a2', 'a(ii)', 'If not listed: are there listed (or publicly traded) entities in the group?', {
              yesno: true, tip: 'If there are listed entities in the group, proceed to (b). If the potential client is not listed and there are no listed entities in the group, proceed to (d).',
              na: [...auditSection('q6_indep_type'), { when: (a) => !no(a.p6_a1_yn), value: NA_LISTED }],
            }),
            proc('p6_b', '(b)', 'If there are listed entities in the group, investigate whether the firm or other PKF Member Firms provide audit or review services to these listed entities.', {
              yesno: true, tip: ['This investigation shall include direct enquiry of client management and review of the transnational entity listing and may be supplemented by an email to the Member Firm(s) geographically close to these listed entities (this is not required).', 'If the firm or other Member Firms provide audit or review services to listed entities in the group, proceed to (c). If not, proceed to (d).'],
              na: [...auditSection('q6_indep_type'), { when: (a) => !(no(a.p6_a1_yn) && yes(a.p6_a2_yn)), value: SKIP_A }],
            }),
            proc('p6_c', '(c)', 'If the potential client is listed, or the firm or other Member Firms provide audit or review services to listed entities in the group, independence considerations must include all related entities as defined in the IESBA Code for Professional Accountants. Enquire whether the firm or other Member Firms provide non-assurance services to the potential client or any related entities.', {
              yesno: true, tip: ['This enquiry shall be direct enquiry of client management and review of the transnational entity listing and may be supplemented by an email to the Member Firm(s) geographically close to these related entities.', 'If the firm or other Member Firms provide non-assurance services to the potential client or any related entities, proceed to (f). If not, no further action is required.'],
              na: [...auditSection('q6_indep_type'), { when: (a) => !(yes(a.p6_a1_yn) || yes(a.p6_b_yn)), value: SKIP_AB }],
            }),
            proc('p6_d', '(d)', 'If the potential client is not listed and there are no listed entities in the group, or the firm or other Member Firms do not provide audit or review services to any listed entities in the group, enquire whether the firm or other Member Firms provide non-assurance services to the potential client or entities over which the potential client has direct or indirect control.', {
              yesno: true, tip: ['This enquiry shall be direct enquiry of client management and review of the transnational entity listing and may be supplemented by an email to the Member Firm(s) geographically close to these entities.', 'If the firm or other Member Firms provide non-assurance services to the potential client or entities over which the potential client has direct or indirect control, proceed to (f). If not, proceed to (e).'],
              na: [...auditSection('q6_indep_type'), { when: (a) => !((no(a.p6_a1_yn) && no(a.p6_a2_yn)) || no(a.p6_b_yn)), value: SKIP_AB }],
            }),
            proc('p6_e', '(e)', 'Is there reason to believe that a relationship or circumstance involving any other related entity of the potential client is relevant to the evaluation of the firm\'s independence from the client?', {
              yesno: true, tip: 'If yes, apply step (f) below to that related entity. If not, no further action is required.',
              na: [...auditSection('q6_indep_type'), { when: (a) => !no(a.p6_d_yn), value: SKIP_D }],
            }),
            proc('p6_f', '(f)', 'Evaluate the significance of the threats identified and apply safeguards when necessary to eliminate the threat or reduce it to an acceptable level. This may include not accepting the engagement.', {
              tip: 'Carry out the evaluation under the IESBA International Code of Ethics for Professional Accountants, including International Independence Standards, and any local ethical code if it is stricter.',
              na: [...auditSection('q6_indep_type'), { when: (a) => !(yes(a.p6_c_yn) || yes(a.p6_d_yn) || yes(a.p6_e_yn)), value: SKIP_F }],
            }),
          ],
        },
        {
          id: 'p6_s6b', title: 'Non-assurance Engagements only – Firm and Network Independence', subtitle: 'When proposing for a non-assurance engagement:',
          items: [
            proc('p6_n_a1', 'a(i)', 'Is the potential client listed (public)?', {
              yesno: true, tip: 'If not, research the family tree and ascertain if there is a listed (or publicly traded) entity or entities in the group.',
              na: nasSection('q6_indep_type'),
            }),
            proc('p6_n_a2', 'a(ii)', 'If not listed: are there other listed entities in the group?', {
              yesno: true, tip: 'If the potential client is listed or there are other listed entities in the group, proceed to (b). If the potential client is not listed and there are no listed entities in the group, proceed to (c).',
              na: [...nasSection('q6_indep_type'), { when: (a) => !no(a.p6_n_a1_yn), value: NA_LISTED }],
            }),
            proc('p6_n_b', '(b)', 'If the potential client is listed or there are other listed entities in the group, investigate whether the firm or other PKF Member Firms provide audit or review services to these listed entities.', {
              yesno: true, tip: ['This investigation shall include direct enquiry of client management and review of the transnational entity listing and may be supplemented by an email to the Member Firm(s) geographically close to these listed entities.', 'If the firm or other Member Firms do not provide audit or review services to listed entities in the group, proceed to (c). If they do, independence considerations must include all related entities as defined in the IESBA International Code for Professional Accountants, including International Independence Standards — proceed to (d).'],
              na: [...nasSection('q6_indep_type'), { when: (a) => !(yes(a.p6_n_a1_yn) || yes(a.p6_n_a2_yn)), value: SKIP_A }],
            }),
            proc('p6_n_c', '(c)', 'If the potential client is not listed and there are no other listed entities in the group, or the firm or other Member Firms do not provide audit or review services to the listed entities in the group, enquire whether the firm or other Member Firms provide audit or review services to the potential client or entities that have direct or indirect control over the potential client.', {
              yesno: true, tip: ['This enquiry shall be direct enquiry of the client management and review of the transnational entity listing and may be supplemented by an email to the Member Firm(s) geographically close to these entities.', 'If the firm or other Member Firms provide audit or review services to the potential client or entities that have direct or indirect control over the potential client, proceed to (d). If not, no further action is required.'],
              na: [...nasSection('q6_indep_type'), { when: (a) => !((no(a.p6_n_a1_yn) && no(a.p6_n_a2_yn)) || no(a.p6_n_b_yn)), value: SKIP_AB }],
            }),
            proc('p6_n_d', '(d)', 'Evaluate the significance of the threats identified and apply safeguards when necessary to eliminate the threat or reduce it to an acceptable level. This may include not accepting the engagement.', {
              tip: 'Carry out the evaluation under the IESBA International Code for Professional Accountants, including International Independence Standards, and any local ethical code if it is stricter.',
              na: [...nasSection('q6_indep_type'), { when: (a) => !(yes(a.p6_n_b_yn) || yes(a.p6_n_c_yn)), value: SKIP_F_NAS }],
            }),
          ],
        },
      ],
    },
    conclusion('q6', {
      decisionLabel: 'Should the new engagement or client be accepted or rejected?',
      decisionOptions: ['Accepted', 'Rejected'],
      strLabel: 'Should a suspicious transaction report ("STR") be filed with CAD for the prospective client?',
      notes: null,
    }),
  ],
};

// ══════════════════════════════════════════════════════════════════════════════
// QC7 — Continuing engagement / client evaluation checklist
// ══════════════════════════════════════════════════════════════════════════════
const QC7_FORM = {
  qcType: 'QC7',
  formNo: 'FORM QC7(4)',
  title: 'Continuing engagement / client evaluation checklist',
  tableTitle: 'CONTINUING ENGAGEMENT / CLIENT EVALUATION CHECKLIST',
  clientLabel: 'CLIENT',
  periodLabel: 'PERIOD ENDED',
  blocks: [
    {
      id: 'particulars',
      title: 'Client & Engagement Particulars',
      sections: [{
        id: 'particulars_main',
        items: [
          { id: 'q7_prior_fee', label: 'Prior year\'s fee', type: 'money', required: true },
          { id: 'q7_prior_recovery', label: 'Prior year\'s recovery rate', type: 'percent', required: true, tip: 'Net fee / time costs.' },
          { id: 'q7_unpaid_audit', label: 'Any outstanding unpaid audit fees?', type: 'yesno', required: true, tip: 'Check to AR aging listing.' },
          followUp('q7_unpaid_audit_amount', 'Outstanding audit fees — amount', 'money', 'q7_unpaid_audit'),
          { id: 'q7_activities', label: 'Type of activities of client', type: 'textarea', required: true },
          { id: 'q7_prior_risk', label: 'Risk rating in prior year', type: 'radio', options: RISK_OPTIONS, required: true, split: true, splitPick: 'highest' },
          { id: 'q7_prior_str', label: 'Any Suspicious Transaction Report filed with CAD in prior year(s)?', type: 'yesno', required: true },
          followUp('q7_prior_str_details', 'Details of the STR filed', 'textarea', 'q7_prior_str'),
          { id: 'q7_prior_opinion', label: 'Prior year\'s audit opinion', type: 'text', required: true },
          { id: 'q7_ep_first_year', label: 'Is this the engagement partner\'s first year audit?', type: 'yesno', required: true, tip: 'If yes, provide the reason for the change.' },
          followUp('q7_ep_change_reason', 'Reason for the change of engagement partner', 'textarea', 'q7_ep_first_year'),
          { id: 'q7_prior_eqr', label: 'Name of Engagement Quality Reviewer in prior year (if applicable)', type: 'text' },
          { id: 'q7_has_nas', label: 'Non audit fee from tax services, accounting, valuation, secretarial services and other non-audit services (NAS) (if any)?', type: 'yesno', required: true, tip: TIP_NAS },
          followUp('q7_nas_service_types', 'Non-audit services (NAS) type(s)', 'text', 'q7_has_nas', { na: { when: (a) => !yes(a.q7_has_nas), value: NA_NO_NAS } }),
          followUp('q7_nas_fee', 'Non-audit services (NAS) fee', 'money', 'q7_has_nas', { na: { when: (a) => !yes(a.q7_has_nas), value: NA_NO_NAS } }),
          { id: 'q7_unpaid_nas', label: 'Any outstanding unpaid non-audit fees?', type: 'yesno', required: true, tip: 'Check to AR aging listing or enquire with the finance department.' },
          followUp('q7_unpaid_nas_amount', 'Outstanding non-audit fees — amount', 'money', 'q7_unpaid_nas'),
          followUp('q7_unpaid_nas_services', 'Outstanding non-audit fees — service type(s)', 'text', 'q7_unpaid_nas'),
          { id: 'q7_fee_concentration', label: 'Fee concentration', type: 'percent', required: true, tip: TIP_FEE_CONCENTRATION, na: { when: (a) => !yes(a.q7_has_nas), value: NA_NO_NAS } },
          { id: 'q7_proposed_fee', label: FEE_LABEL, type: 'money', required: true, split: true },
          { id: 'q7_proposed_recovery', label: 'Proposed recovery rate for current year', type: 'percent', required: true, tip: TIP_RECOVERY },
          { id: 'q7_recovery_reason', label: 'Reasons for acceptance (proposed recovery rate below 30%)', type: 'textarea', required: (a) => recoveryLow(a.q7_proposed_recovery), na: { when: (a) => recoveryOk(a.q7_proposed_recovery), value: NA_RECOVERY } },
          ...pieTneTna('q7'),
        ],
      }],
    },
    {
      id: 'procedures',
      title: 'Objectives and detailed tests',
      sections: [
        {
          id: 'p7_s1', title: 'Objective – To ensure that recurring work for the client does not involve unacceptable risks',
          items: [
            proc('p7_1', 1, 'Decide whether we can rely on the management\'s integrity and whether association with the client may damage the firm\'s reputation.', {
              tip: [...TIP_INTEGRITY_CONSIDER,
                'Suggested wording — "We have carried out the following procedures for our evaluation: we have obtained and reviewed Questnet (Bizfile) profile searches [Appendix _]; we have screened in Google search the names of the Company, ultimate beneficial owner and directors [Appendix _]; we have screened in LexisNexis the names of the Company, ultimate beneficial owner and directors [Appendix _]; we did not identify any conflict of interest in (TREND) (PKFI) for conflict search [Appendix _]; we are not aware of any significant matters that would give rise to concerns over the integrity of management or to reputation issues by our association with the Company."'],
            }),
            proc('p7_2', 2, 'If we have doubts resulting from the considerations above, decide whether the potential risks can be mitigated by introducing additional precautions.', {
              tip: ['Such as: additional procedures; or additional review by an engagement quality reviewer (i.e. safeguard reviewer).',
                'Suggested wording — "From the above information, we have not noted any indicators that would cause concern for us in taking up this audit engagement."'],
            }),
          ],
        },
        {
          id: 'p7_s2', title: 'Objective – To ensure that the firm can comply with relevant ethical requirements',
          items: [
            proc('p7_3', 3, 'Consider whether the firm can comply with relevant ethical requirements, including whether there have been any changes in the firm\'s independence.', {
              tip: 'Include in your consideration potential independence or conflicts of interest problems arising from relationships with other clients, firm members, or their families; providing non-assurance services; or unpaid fees.',
            }),
            proc('p7_4', 4, 'If there has been any change in the relationship between the firm and the client or any associated companies, decide whether we are still, and can be seen still to be, independent of the client.', {
              tip: [...TIP_INDEPENDENCE_CONSIDER,
                'Suggested wording — "We have carried out the following procedures: we did not identify any conflict of interest in TREND (PKFI) for conflict search [Appendix _]; we have not noted any threats to independence and / or conflicts of interest with the Companies; we are not aware of any independence or conflict of interest that requires additional precautions. We conclude that we are in compliance with the relevant ethical requirements, independence."'],
            }),
          ],
        },
        {
          id: 'p7_s3', title: 'Objective – To ensure that the firm has the ability to perform the professional services properly',
          items: [
            proc('p7_5', 5, 'Decide whether we have the competence to carry out the professional services properly. Consider whether special skills are needed to deal with the particular features or specialised reporting requirements of the client.', {
              tip: 'Suggested wording — "We have the necessary competency to carry out the engagement. Specialists involved: [e.g. FAS / IT]."',
            }),
            proc('p7_6', 6, 'Confirm that the firm will have adequate resources to be able to do the work properly at the time the client needs it.', {
              tip: 'Suggested wording — "We have the necessary resources to carry out the engagement."',
            }),
            proc('p7_7', 7, 'Decide whether the level of fees agreed will be adequate to allow us to carry out the work deemed necessary, properly and with integrity.'),
            proc('p7_8', 8, 'Consider the client\'s ability to pay the agreed fees.', {
              tip: 'Suggested wording — "Yes, the fees were settled promptly last year." / "There are no outstanding fees."',
            }),
          ],
        },
        {
          id: 'p7_s4', title: 'Objective – To ensure that the client\'s understanding of the scope and terms of the engagement agrees with our own and no events have occurred to adversely affect our relationship with the client',
          items: [
            proc('p7_9', 9, 'Consider whether events have occurred which may affect the firm\'s relationship with the client.', {
              tip: ['This should be done if there is: a significant change in management or ownership; a change in the client\'s legal advisers; an adverse change in the client\'s financial condition; significant litigation against the client by a third party; a change in the nature of the business; a change in the scope of work to be carried out; or a change in the firm\'s responsibilities to any regulatory authority.',
                'Ensure appropriate procedures are carried out in response to the above matters, including a re-evaluation of the firm\'s independence, a reconsideration of management\'s integrity and a reconsideration of responsibilities in relation to money laundering if relevant.',
                'Suggested wording — "Based on our discussion with Management [Name of client and position], we have not noted any significant events (with respect to nature of business operations, ownership structure, accounting and IT environment) that have occurred which may affect the firm\'s relationship with the Company."'],
            }),
          ],
        },
        {
          id: 'p7_s5', title: 'Objective – To ensure that the client\'s understanding of the scope and terms of the engagement is agreed',
          items: [
            proc('p7_10', 10, 'Decide whether we need to renew our engagement letter. If yes, send a revised letter and obtain the client\'s agreement to its terms before starting work.', { yesno: true }),
          ],
        },
        {
          id: 'p7_s6', title: 'Objective – To establish the identity of the client to assist in identifying possible instances of money laundering ("AML") and terrorism financing activities ("CTF")',
          items: [
            proc('p7_11', 11, 'For anti-money laundering ("AML") and counter-terrorism financing ("CTF") activities, carry out the screening procedures and document the results.', { tip: TIP_AML('QC7(4)') }),
            proc('p7_12', 12, 'Are the directors identified to be a Politically Exposed Person ("PEP")? If yes, establish where the source of funds is obtained.', {
              yesno: true, naScope: 'comment', tip: TIP_PEP, na: { when: (a) => no(a.p7_12_yn), value: NA_NO_PEP },
            }),
            proc('p7_13', 13, 'Are the ultimate beneficial owners (individuals owning more than 25% equity interest in the ultimate holding company) a PEP? If yes, establish where the source of funds is obtained.', {
              yesno: true, naScope: 'comment', tip: TIP_PEP, na: { when: (a) => no(a.p7_13_yn), value: NA_NO_PEP },
            }),
          ],
        },
        {
          id: 'p7_s7', title: 'Firm and Network Independence – Continuance Procedures',
          tip: TIP_FIRM_NETWORK_HEADER,
          items: [
            { id: 'q7_indep_type', label: 'This engagement is', type: 'radio', options: INDEP_TYPE_OPTIONS, required: true, tip: 'Audit and review engagements complete the first section; non-assurance engagements complete the second. The section that does not apply is marked "Not applicable".' },
            { id: 'q7_nas_type', label: 'Type of non-assurance service', type: 'radio', options: NAS_TYPE_OPTIONS, required: true, tip: TIP_NAS_TYPE, na: nasSection('q7_indep_type') },
          ],
        },
        {
          id: 'p7_s7a', title: 'Audit and Review Engagements only – Firm and Network Independence',
          items: [
            proc('p7_cont_audit', '', 'When deciding whether or not to continue with an audit or review engagement: have any changes been identified that alter the original assessment of the acceptability of the client and engagement, including network firm independence?', {
              yesno: true,
              tip: ['Examples of changes that shall cause a careful reconsideration are: a significant change in the size, structure or nature of the client\'s business; a major change in the ownership or management of the client; and new regulatory reporting requirements.',
                'Network conflict checking shall require direct enquiry of the client management to determine whether there were any: changes in the controlling ownership interests over the entity; changes in any controlling interests held by the entity; new listings in the group; any known change in service providers that may impact independence; or changes in classification of the entity as public interest.',
                'Any such changes will require reconsideration of the relevant firm and network conflict checking procedures as outlined under the acceptance procedures in Form QC6(4).'],
              na: auditSection('q7_indep_type'),
            }),
          ],
        },
        {
          id: 'p7_s7b', title: 'Non-assurance Engagements only – Firm and Network Independence',
          items: [
            proc('p7_cont_nas', '', 'When deciding whether or not to continue with a non-assurance engagement: have any changes been identified that alter the original assessment of the acceptability of the client and engagement, including network firm independence?', {
              yesno: true,
              tip: ['Examples of changes that shall cause a careful reconsideration are: a significant change in the size, structure or nature of the client\'s business; a major change in the ownership or management of the client; and new regulatory reporting requirements.',
                'Network conflict checking shall require direct enquiry of the client management to determine whether there were any: changes in the controlling ownership interests over the entity; changes in any controlling interests held by the entity; new listings in the group; any known change in service providers that may impact independence; or changes in classification of the entity as public interest.',
                'Any such changes will require reconsideration of the relevant firm and network conflict checking procedures as outlined under the acceptance procedures in Form QC6(4).'],
              na: nasSection('q7_indep_type'),
            }),
          ],
        },
      ],
    },
    conclusion('q7', {
      decisionLabel: 'Should the engagement or client be retained or rejected?',
      decisionOptions: ['Retained', 'Rejected'],
      strLabel: 'Should a suspicious transaction report ("STR") be filed with CAD for the client?',
      notes: [
        'Any changes to the risk rated in Form QC6 or Form QC7 during the progress of the engagement by the partner should be explained and documented as an addendum to Form QC6 and Form QC7. The reassessment of risk to a different category should be revised and approved following the approval matrix. The addendum to Form QC6 or Form QC7 should be submitted to the Quality Management Team.',
        'Screening shall be performed for any newly appointed director / UBO during the course of the engagement, if any.',
      ],
    }),
  ],
};

const FORMS = { QC6: QC6_FORM, QC7: QC7_FORM };

// ── Structure access ─────────────────────────────────────────────────────────

/** The whole form definition (header labels + blocks). Unknown type → QC6. */
const getQcForm = (qcType) => FORMS[String(qcType || '').toUpperCase()] || QC6_FORM;

/** Every item of the form, in reading order (sections flattened). */
const getQcItems = (qcType) =>
  getQcForm(qcType).blocks.flatMap((b) => b.sections.flatMap((s) => s.items));

// Kept for callers that iterate "questions" generically — same as the items.
const getQcQuestions = getQcItems;

/**
 * A select with an `other` choice keeps the free-text specification in
 * `<id>_other`, shown in the same cell under the dropdown. It is required
 * only while that choice is picked and cleared otherwise.
 */
const otherIdOf = (item) => `${item.id}_other`;
const otherPicked = (item, answers) => !!item.other && (answers || {})[item.id] === item.other.value;

// ── Per-engagement split ─────────────────────────────────────────────────────
// A form covering several engagements records one value per covered
// engagement under `<id>_split` = { <engagementId>: value }, next to `<id>`:
// all or none — once one engagement has a value every one needs it. `<id>`
// then holds the one value the rest of the form reads (splitDerivedValue):
//   Estimated fees            the sum (with none, the Total the requester types)
//   Expected FS Signing Date  the earliest (with none, every engagement's date)
//   PIE?                      Yes when any engagement is a PIE
//   Type of PIE               the types, joined; follows PIE per engagement
//   Risk rating (and prior)   the highest — the EQR follows the riskiest one
//   Reason for the rating     the reasons, joined
// Read one engagement's own answer with qcAnswerForEngagement.
// Single-engagement forms carry no split. Keys are engagement ids as strings.
const splitIdOf = (item) => `${item.id}_split`;
const feeItemIdOf = (qcType) => (String(qcType || '').toUpperCase() === 'QC7' ? 'q7_proposed_fee' : 'q6_est_fee');
/** The engagement id of a covered-engagement entry (slim list `id`, records `engagementId`). */
const coveredEngagementIdOf = (e) => (e && (e.engagementId !== undefined && e.engagementId !== null ? e.engagementId : e.id));
/** A split object as stored, or {} (anything not a plain object reads as none). */
const splitOf = (answers, id) => {
  const v = (answers || {})[id];
  return v && typeof v === 'object' && !Array.isArray(v) ? v : {};
};
/** A split answered per engagement with a choice or a text (not an amount, not a date). */
const isChoiceSplit = (item) => !!item && !!item.split && item.type !== 'money' && item.type !== 'date';
/** The per-engagement "if Others" specifications of a split select with an `other` choice. */
const otherSplitIdOf = (item) => `${otherIdOf(item)}_split`;
/**
 * A split item's own value as its per-engagement values decide it (see the
 * table above) — null when the item has no split values.
 */
const splitDerivedValue = (item, answers) => {
  if (!item.split) return null;
  const split = splitOf(answers, splitIdOf(item));
  if (item.type === 'money') return splitSum(split);
  if (item.type === 'date') return splitEarliest(split);
  const vals = Object.values(split).filter((v) => !blank(v)).map((v) => String(v).trim());
  if (vals.length === 0) return null;
  if (item.type === 'yesno') return vals.includes('Yes') ? 'Yes' : 'No';
  if (item.splitPick === 'highest') {
    const opts = item.options || [];
    return vals.reduce((m, v) => (opts.indexOf(v) > opts.indexOf(m) ? v : m));
  }
  // Only N/A values (no engagement it applies to has answered yet) decide nothing.
  const own = [...new Set(vals.filter((v) => !isAutoValue(v)))];
  if (own.length === 0) return null;
  return own.join(item.type === 'textarea' ? '\n\n' : ' / ');
};
/**
 * One engagement's own answer to a question: its per-engagement value when the
 * form split the question, else the form's one answer.
 */
const qcAnswerForEngagement = (answers, id, engagementId) => {
  const a = answers || {};
  const own = engagementId === null || engagementId === undefined ? undefined : splitOf(a, `${id}_split`)[String(engagementId)];
  return blank(own) ? a[id] : own;
};
/**
 * A split that follows another one engagement by engagement (Type of PIE ←
 * PIE): with the leading question split, every engagement it answers gets
 * the follower's N/A value where it does not apply, and keeps — or starts
 * from the shared answer — where it does. With the leader no longer split, a
 * follower still holding those N/A values drops its split.
 * Returns the new split, or undefined when nothing changes.
 */
const followedSplitOf = (item, a) => {
  const f = item.splitFollows;
  if (!f) return undefined;
  const lead = splitOf(a, `${f.id}_split`);
  const own = splitOf(a, splitIdOf(item));
  let next;
  if (Object.keys(lead).length === 0) {
    next = Object.values(own).some((v) => v === f.value) ? {} : own;
  } else {
    next = {};
    const shared = a[item.id];
    Object.keys(lead).forEach((eid) => {
      if (!yes(lead[eid])) next[eid] = f.value;
      else if (!blank(own[eid]) && own[eid] !== f.value) next[eid] = own[eid];
      else if ((item.options || []).includes(shared)) next[eid] = shared;
    });
  }
  return JSON.stringify(next) === JSON.stringify(own) ? undefined : next;
};
/** The "if Others" specifications kept only for the engagements that picked Others. */
const followedOtherSplitOf = (item, a) => {
  if (!item.other || !item.split) return undefined;
  const picks = splitOf(a, splitIdOf(item));
  const specs = splitOf(a, otherSplitIdOf(item));
  const next = {};
  Object.entries(specs).forEach(([eid, v]) => { if (picks[eid] === item.other.value && !blank(v)) next[eid] = v; });
  return JSON.stringify(next) === JSON.stringify(specs) ? undefined : next;
};
/** The earliest ISO date in a split, or null when it holds none. */
const splitEarliest = (split) => {
  const vals = Object.values(split || {}).filter((v) => !blank(v)).map((v) => String(v).trim()).sort();
  return vals.length > 0 ? vals[0] : null;
};
/** The sum of a split's amounts as a string (cent-exact), or null when it holds none. */
const splitSum = (split) => {
  const vals = Object.values(split || {}).filter((v) => !blank(v)).map(Number);
  if (vals.length === 0 || vals.some((n) => !Number.isFinite(n))) return null;
  return String(vals.reduce((t, n) => t + Math.round(n * 100), 0) / 100);
};
const fmtAmount = (v, ccy) => {
  const n = Number(v);
  const shown = Number.isFinite(n) ? n.toLocaleString(undefined, { maximumFractionDigits: 2 }) : String(v);
  return blank(ccy) ? shown : `${ccy} ${shown}`;
};

const splitFieldOf = (item) => ({ id: splitIdOf(item), label: `${item.label} — per engagement`, type: 'split', item });
const otherSplitFieldOf = (item) => ({
  id: otherSplitIdOf(item), label: `${item.other.label} — per engagement`, type: 'split',
  item: { id: otherIdOf(item), label: item.other.label, type: 'text' },
});

/** The logical answer fields an item occupies (money / procedure / other expand). */
const fieldsOfItem = (item) => {
  if (item.type === 'note') return [];
  if (item.type === 'money') {
    return [
      { id: item.id, label: `${item.label} — amount`, type: 'number', item },
      { id: `${item.id}_ccy`, label: `${item.label} — currency`, type: 'text', item },
      ...(item.split ? [splitFieldOf(item)] : []),
    ];
  }
  if (item.other) {
    return [
      { id: item.id, label: item.label, type: item.type, item },
      { id: otherIdOf(item), label: item.other.label, type: 'text', item, otherOf: item },
      ...(item.split ? [splitFieldOf(item), otherSplitFieldOf(item)] : []),
    ];
  }
  if (item.type === 'procedure') {
    const prefix = item.no !== '' && item.no !== undefined ? `${item.no}. ` : '';
    const out = [];
    if (item.yesno) out.push({ id: `${item.id}_yn`, label: `${prefix}${item.label} — Yes / No`, type: 'yesno', item });
    out.push({ id: `${item.id}_comment`, label: `${prefix}${item.label} — Comment`, type: 'textarea', item });
    return out;
  }
  return [{ id: item.id, label: item.label, type: item.type, item }, ...(item.split ? [splitFieldOf(item)] : [])];
};

/** Flattened answerable fields — validation, read-only views, diffs, comments. */
const answerableQuestions = (qcType) => getQcItems(qcType).flatMap(fieldsOfItem);

/** `answers.<id>` → the field's wording (HistoryCard / reviewer comments). */
const qcAnswerLabels = (qcType) => Object.fromEntries(
  answerableQuestions(qcType).map((f) => [`answers.${f.id}`, f.label])
);

// ── Not-applicable evaluation ────────────────────────────────────────────────

/** The N/A rule that currently applies to an item, or null. */
const naState = (item, answers = {}) => {
  if (!item.na) return null;
  const rules = Array.isArray(item.na) ? item.na : [item.na];
  for (const r of rules) if (r.when(answers)) return r;
  return null;
};

const NA_VALUES = new Set([
  NA, NA_NOT_PIE, NA_NO_NAS, NA_NO_PEP, NA_RECOVERY, NA_AUDIT_SECTION, NA_NAS_SECTION,
  NA_LISTED, NA_CHOOSE_TYPE, NA_CHOOSE_RATING, SKIP_A, SKIP_A_OLD, SKIP_AB, SKIP_D, SKIP_F, SKIP_F_NAS, EQR_NOT_REQUIRED,
]);
const isAutoValue = (v) => typeof v === 'string' && NA_VALUES.has(v);

/** Which logical field carries an item's N/A value. */
const naTarget = (item) => (item.type === 'procedure' ? `${item.id}_comment` : item.id);

/**
 * Bring the auto-filled answers into line with the answers they depend on:
 *   - every item whose `na` rule matches gets its N/A value (a procedure's
 *     Yes/No clears unless naScope is 'comment');
 *   - an item whose rule no longer matches has the auto value cleared again;
 *   - money items default to SGD;
 *   - an `other` specification clears unless its choice is picked.
 * Runs to a fixed point (a section rule feeds the branch rules inside it).
 */
const applyAutoAnswers = (answers, qcType) => {
  const items = getQcItems(qcType);
  const state = { a: Object.assign({}, answers || {}), changed: false };
  const set = (k, v) => {
    if (state.a[k] === v) return;
    state.a = Object.assign({}, state.a, { [k]: v });
    state.changed = true;
  };
  const clear = (k) => {
    if (state.a[k] === undefined) return;
    const next = Object.assign({}, state.a);
    delete next[k];
    state.a = next;
    state.changed = true;
  };
  // A split object: {} clears the key.
  const setSplit = (k, v) => { if (Object.keys(v).length === 0) clear(k); else set(k, v); };
  const pass = () => {
    state.changed = false;
    items.forEach((item) => {
      if (item.type === 'note') return;
      const target = naTarget(item);
      const st = naState(item, state.a);
      if (st) {
        set(target, st.value);
        if (item.type === 'procedure' && item.naScope !== 'comment') {
          if (item.yesno) clear(`${item.id}_yn`);
        }
        if (item.type === 'money') clear(`${item.id}_ccy`);
        if (item.split) clear(splitIdOf(item));
        if (item.other) clear(otherIdOf(item));
        if (item.other && item.split) clear(otherSplitIdOf(item));
      } else {
        if (isAutoValue(state.a[target])) clear(target);
        if (item.type === 'money' && blank(state.a[`${item.id}_ccy`])) set(`${item.id}_ccy`, DEFAULT_CURRENCY);
        const followed = followedSplitOf(item, state.a);
        if (followed !== undefined) setSplit(splitIdOf(item), followed);
        const derived = splitDerivedValue(item, state.a);
        if (derived !== null) set(item.id, derived);
        if (item.other && !otherPicked(item, state.a)) clear(otherIdOf(item));
        const specs = followedOtherSplitOf(item, state.a);
        if (specs !== undefined) setSplit(otherSplitIdOf(item), specs);
      }
    });
  };
  for (let i = 0; i < 6; i += 1) {
    pass();
    if (!state.changed) break;
  }
  return state.a;
};

/** Ids the form must render locked: the ones currently N/A. */
const lockedFieldIds = (answers, qcType) => {
  const locked = new Set();
  getQcItems(qcType).forEach((item) => {
    const st = naState(item, answers || {});
    if (!st) return;
    if (item.type === 'procedure') {
      locked.add(`${item.id}_comment`);
      if (item.naScope !== 'comment') {
        if (item.yesno) locked.add(`${item.id}_yn`);
      }
    } else {
      locked.add(item.id);
      if (item.type === 'money') locked.add(`${item.id}_ccy`);
      if (item.split) locked.add(splitIdOf(item));
      if (item.other) locked.add(otherIdOf(item));
      if (item.other && item.split) locked.add(otherSplitIdOf(item));
    }
  });
  return locked;
};

// ── Read-only helpers ────────────────────────────────────────────────────────

// Visibility check for question specs that carry `showIf`; QC6/QC7 items
// are always visible, so this only matters to the form editor's guard.
const isVisible = (q, answers = {}) => {
  if (!q || !q.showIf) return true;
  const { id, equals, hasValue, lt, oneOf } = q.showIf;
  const v = answers[id];
  const isBlank = v === undefined || v === null || String(v).trim() === '';
  if (hasValue) return !isBlank;
  if (lt !== undefined) return !isBlank && Number(v) < lt;
  if (equals !== undefined) return v === equals;
  if (Array.isArray(oneOf)) return oneOf.includes(v);
  return true;
};

// ISO 'YYYY-MM-DD' → 'DD Month YYYY'. Local to this CommonJS bank (the server
// PDF renders through it too); anything not ISO passes through untouched.
const QC_MONTHS = ['January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'];
const fmtQcDate = (v) => {
  const m = String(v).trim().match(/^(\d{4})-(\d{2})-(\d{2})/);
  return m ? `${m[3]} ${QC_MONTHS[Number(m[2]) - 1] || m[2]} ${m[1]}` : String(v);
};

/** One per-engagement value as shown: a date in words, an amount with its currency, else as answered. */
const fmtSplitValue = (item, v, ccy) => {
  if (item && item.type === 'date') return fmtQcDate(v);
  if (item && item.type === 'money') return fmtAmount(v, ccy);
  return String(v);
};

/** One read-only rendering of a logical field's value. */
const fmtQcAnswerValue = (q, v) => {
  if (q && q.type === 'checkbox') return v === true ? 'Confirmed' : 'Not confirmed';
  if (q && q.type === 'split') {
    const entries = Object.entries(v && typeof v === 'object' && !Array.isArray(v) ? v : {}).filter(([, x]) => !blank(x));
    return entries.length === 0 ? '—' : entries.map(([k, x]) => `#${k}: ${fmtSplitValue(q.item, x)}`).join('; ');
  }
  if (q && q.type === 'percent' && !blank(v) && !isAutoValue(v)) return `${v} %`;
  if (q && q.type === 'date' && !blank(v) && !isAutoValue(v)) return fmtQcDate(v);
  return (v === undefined || v === null || String(v).trim() === '') ? '—' : String(v);
};

/** A money item's amount + currency as one string. */
const fmtMoney = (item, answers = {}) => {
  const amount = answers[item.id];
  if (blank(amount)) return '—';
  if (isAutoValue(amount)) return String(amount);
  const n = Number(amount);
  const shown = Number.isFinite(n) ? n.toLocaleString(undefined, { maximumFractionDigits: 2 }) : String(amount);
  const ccy = answers[`${item.id}_ccy`];
  return blank(ccy) ? shown : `${ccy} ${shown}`;
};

/**
 * A whole item's value as the one string its cell shows: money as currency +
 * amount, an `other` choice followed by its specification.
 */
const fmtItemValue = (item, answers = {}) => {
  if (item.type === 'money') return fmtMoney(item, answers);
  const shown = fmtQcAnswerValue(item, answers[item.id]);
  const spec = answers[otherIdOf(item)];
  return otherPicked(item, answers) && !blank(spec) ? `${shown} — ${String(spec).trim()}` : shown;
};

/** { <engagementId>: name } from one or more covered-engagement lists (History labels). */
const engagementNameMap = (...lists) => {
  const out = {};
  lists.forEach((list) => (list || []).forEach((e) => {
    const id = coveredEngagementIdOf(e);
    if (id !== undefined && id !== null) {
      out[String(id)] = e.engagementName ? engagementDisplayName(e.engagementName, e.serviceDescription) : (e.entityName || `Engagement #${id}`);
    }
  }));
  return out;
};

/**
 * A split item's per-engagement lines, in coverage order:
 * [{ engagementId, name, title, subtitle, amount, text }] — `amount` is the engagement's own
 * value (null when it has none); `text` is that value as shown (currency +
 * amount, or the date in words), or '—'. `engagements` are the covered
 * engagements (slim list or record shape).
 */
const splitLines = (item, answers = {}, engagements = []) => {
  const split = splitOf(answers, splitIdOf(item));
  const ccy = answers[`${item.id}_ccy`];
  return (engagements || []).map((e) => {
    const id = String(coveredEngagementIdOf(e));
    const v = split[id];
    // "Company — Service #n · description" (engagementTag): two engagements of one company stay apart.
    const service = engagementTag(e.serviceProvided, e.engagementSeq, e.serviceDescription);
    const name = !blank(e.entityName)
      ? (service ? `${e.entityName} — ${service}` : e.entityName)
      : (e.engagementName || `Engagement #${id}`);
    return {
      engagementId: id,
      name,
      // The same name in two parts for the input lists: company, then service.
      title: !blank(e.entityName) ? e.entityName : name,
      subtitle: !blank(e.entityName) ? service : '',
      amount: blank(v) ? null : v,
      text: blank(v) ? '—' : fmtSplitValue(item, v, ccy),
    };
  });
};

/**
 * The per-engagement lines of a choice / text split (PIE, Type of PIE, the
 * rating and its reason) — splitLines with each `text` as the engagement's
 * answer ("Others — <specification>" for a select's other choice) — or null
 * when the item is not answered per engagement on this form.
 */
const choiceSplitLines = (item, answers = {}, engagements = []) => {
  if (!isChoiceSplit(item) || (engagements || []).length < 2) return null;
  const lines = splitLines(item, answers, engagements);
  if (!lines.some((l) => l.amount !== null)) return null;
  const specs = item.other ? splitOf(answers, otherSplitIdOf(item)) : {};
  return lines.map((l) => {
    if (l.amount === null) return Object.assign({}, l, { text: fmtItemValue(item, answers) });
    const spec = item.other && l.amount === item.other.value ? specs[l.engagementId] : null;
    return blank(spec) ? l : Object.assign({}, l, { text: `${l.text} — ${String(spec).trim()}` });
  });
};

/**
 * Replace an object-valued `answers` change with ONE ROW PER FIELD (History
 * diffs; the repositories store whole before/after answer objects). Untouched
 * fields are dropped; fields no longer in the bank still appear under their
 * raw id so a historical answer never silently vanishes.
 */
const expandAnswerChanges = (changes, qcType, { engagementNames = {} } = {}) => {
  const answers = changes && changes.answers;
  if (!answers || typeof answers !== 'object' || !('before' in answers || 'after' in answers)) {
    return changes || {};
  }
  const rest = Object.assign({}, changes);
  delete rest.answers;
  const before = answers.before || {};
  const after = answers.after || {};
  const bank = answerableQuestions(qcType);
  const known = new Set(bank.map((q) => q.id));
  const extras = [...new Set([...Object.keys(before), ...Object.keys(after)])]
    .filter((id) => !known.has(id))
    .map((id) => ({ id, label: id, type: 'text' }));
  const rows = {};
  [...bank, ...extras].forEach((q) => {
    if (q.type === 'split') {
      // One row per engagement whose own value changed.
      const sb = splitOf(before, q.id);
      const sa = splitOf(after, q.id);
      [...new Set([...Object.keys(sb), ...Object.keys(sa)])].forEach((eid) => {
        const b = blank(sb[eid]) ? '—' : fmtSplitValue(q.item, sb[eid]);
        const a = blank(sa[eid]) ? '—' : fmtSplitValue(q.item, sa[eid]);
        if (b === a) return;
        const name = engagementNames[eid] || `Engagement #${eid}`;
        rows[`answers.${q.id}.${eid}`] = { before: b, after: a, label: `${q.item ? q.item.label : q.label} — ${name}` };
      });
      return;
    }
    const b = fmtQcAnswerValue(q, before[q.id]);
    const a = fmtQcAnswerValue(q, after[q.id]);
    if (b === a) return;
    rows[`answers.${q.id}`] = { before: b, after: a, label: q.label };
  });
  return Object.assign({}, rest, rows);
};

// ── Validation ───────────────────────────────────────────────────────────────

const isRequired = (item, answers) =>
  (typeof item.required === 'function' ? item.required(answers) : !!item.required);

/**
 * Required fields still unanswered, as `[{ key, label }]`. `key` is
 * `answers.<fieldId>` — the `field=` key QcForm's rows and the reviewer's
 * comments use — so a page hands the list to formValidation and the form marks
 * the very rows that are empty; `label` is the banner wording. Locked (N/A /
 * derived) fields are never reported — they are filled by the form itself.
 */
const qcAnswerGaps = (answers, qcType) => {
  const a = answers || {};
  const locked = lockedFieldIds(a, qcType);
  const gaps = [];
  getQcItems(qcType).forEach((item) => {
    if (item.type === 'note' || !isRequired(item, a)) return;
    fieldsOfItem(item).forEach((f) => {
      if (locked.has(f.id)) return;
      if (f.id.endsWith('_ccy')) return; // defaulted by applyAutoAnswers
      if (f.type === 'split') return; // all-or-none amounts: qcSplitGaps
      // A split select's "Others" is specified per engagement (qcSplitGaps).
      if (f.otherOf && (!otherPicked(item, a) || Object.keys(splitOf(a, splitIdOf(item))).length > 0)) return;
      const v = a[f.id];
      const empty = f.type === 'checkbox' ? v !== true : blank(v);
      if (empty) gaps.push({ key: `answers.${f.id}`, label: f.label });
    });
  });
  return gaps;
};

/**
 * Per-engagement values are all or none: once one covered engagement has an
 * amount (or date), every one needs it — same `[{ key, label }]` shape as
 * qcAnswerGaps, keyed to the item's row. Every QC6 / QC7 surface runs it with
 * its coverage (the legacy-import form included).
 */
const qcSplitGaps = (answers, qcType, engagementIds = [], { requireFee = false } = {}) => {
  const a = answers || {};
  const ids = (engagementIds || []).map((x) => String(x));
  if (ids.length < 2) return [];
  const locked = lockedFieldIds(a, qcType);
  return getQcItems(qcType)
    .filter((item) => item.split && !locked.has(item.id))
    .filter((item) => {
      const split = splitOf(a, splitIdOf(item));
      // Two or more engagements: every engagement enters its own Estimated
      // fees (owner 2026-10-14) — a Total alone is a gap where `requireFee`.
      if (Object.keys(split).length === 0) return requireFee && item.type === 'money';
      if (ids.some((id) => blank(split[id]))) return true;
      // A per-engagement "Others" needs its specification.
      const specs = item.other ? splitOf(a, otherSplitIdOf(item)) : {};
      return !!item.other && ids.some((id) => split[id] === item.other.value && blank(specs[id]));
    })
    .map((item) => ({
      key: `answers.${item.id}`,
      label: `${item.label} — ${item.type === 'date' ? 'date' : item.type === 'money' ? 'amount' : 'answer'} for every engagement`,
    }));
};

/** The same check as a list of labels — for banners that only name the gaps. */
const missingQcAnswers = (answers, qcType) => qcAnswerGaps(answers, qcType).map((g) => g.label);

/** The EQR an approved form assigns to its covered engagements. */
const qcEqrOf = (answers, qcType) => {
  const p = String(qcType || '').toUpperCase() === 'QC7' ? 'q7' : 'q6';
  const v = (answers || {})[`${p}_c_eqr`];
  return blank(v) ? null : String(v).trim();
};

// ── Addendum-only answers ────────────────────────────────────────────────────
// Once a record is approved, the Conclusion's risk rating, its reason and the
// EQR change only through a QC6 / QC7 Addendum (dated, from → to, its own
// sign-off) — never through a QC Amendment or a Management edit, which would
// overwrite the answer and leave only a diff behind. One list, read by the
// form (locks the rows), the QCA / Management validators (400
// ADDENDUM_ONLY_FIELD) and the QCA approval (409 when the world moved).
const ADDENDUM_ONLY_SUFFIXES = ['_c_rating', '_c_rating_reason', '_c_eqr'];
const addendumOnlyAnswerIds = (qcType) => {
  const p = String(qcType || '').toUpperCase() === 'QC7' ? 'q7' : 'q6';
  return ADDENDUM_ONLY_SUFFIXES.map((s) => `${p}${s}`);
};
// The per-engagement rating and reason are addendum-only as well.
const addendumOnlySplitIds = (qcType) => {
  const p = String(qcType || '').toUpperCase() === 'QC7' ? 'q7' : 'q6';
  return [`${p}_c_rating_split`, `${p}_c_rating_reason_split`];
};
/** A split as one comparable string (keys sorted), null when empty. */
const splitKeyOf = (v) => {
  const o = v && typeof v === 'object' && !Array.isArray(v) ? v : {};
  const keys = Object.keys(o).filter((k) => !blank(o[k])).sort();
  return keys.length === 0 ? null : keys.map((k) => `${k}=${String(o[k]).trim()}`).join('|');
};
const ADDENDUM_ONLY_NOTE = 'Change via QC6 / QC7 Addendum';

/**
 * The addendum-only answers an edit would change: [{ id, label, before,
 * after }]. A QC Amendment may touch none of them — not even fill a blank
 * one (imported records are mostly blank there and the amendment's
 * requester is not the one to invent the value). With `allowFill`
 * (Management, the administrator) a blank answer may be filled in; one the
 * record already holds still cannot change nor be cleared. Both sides go
 * through applyAutoAnswers first; an auto-filled placeholder (the EQR's
 * "Choose the risk rating above") reads as blank.
 */
const addendumOnlyChanges = (before, after, qcType, { allowFill = false } = {}) => {
  // The auto pass only decides which answers are N/A placeholders; the VALUE
  // compared is the raw answer — applyAutoAnswers also strips the EQR's
  // "NOT REQUIRED" sentinel (an imported record's usual answer) and that is
  // a value the record holds, not a blank.
  const rawA = before || {};
  const rawB = after || {};
  const naBefore = lockedFieldIds(applyAutoAnswers(rawA, qcType), qcType);
  const naAfter = lockedFieldIds(applyAutoAnswers(rawB, qcType), qcType);
  // A stale placeholder ("Choose the risk rating above") is a blank; the EQR's
  // "NOT REQUIRED" is not — it is what an imported record answered.
  const placeholder = (v) => isAutoValue(v) && v !== EQR_NOT_REQUIRED;
  const norm = (v, na) => (na || blank(v) || placeholder(v) ? null : String(v).trim());
  const labels = new Map(getQcItems(qcType).map((i) => [i.id, i.label]));
  const splits = addendumOnlySplitIds(qcType).map((id) => ({
    id, label: `${labels.get(id.replace(/_split$/, '')) || id} — per engagement`,
    before: splitKeyOf(rawA[id]), after: splitKeyOf(rawB[id]),
  }));
  return [
    ...addendumOnlyAnswerIds(qcType)
      .map((id) => ({ id, label: labels.get(id) || id, before: norm(rawA[id], naBefore.has(id)), after: norm(rawB[id], naAfter.has(id)) })),
    ...splits,
  ].filter((c) => c.before !== c.after && !(allowFill && c.before === null));
};

/**
 * The addendum-only answers a record already HOLDS — the rows an editing
 * surface freezes when blanks may still be filled (Management). Same value
 * reading as addendumOnlyChanges (raw answer; NA placeholders are blank,
 * the EQR's "NOT REQUIRED" is a value).
 */
const addendumOnlyHeldIds = (answers, qcType) => {
  const raw = answers || {};
  const na = lockedFieldIds(applyAutoAnswers(raw, qcType), qcType);
  const placeholder = (v) => isAutoValue(v) && v !== EQR_NOT_REQUIRED;
  return new Set(addendumOnlyAnswerIds(qcType).filter((id) => !na.has(id) && !blank(raw[id]) && !placeholder(raw[id])));
};

/**
 * The estimated fee an acceptance form carries for ONE engagement (the
 * "Estimated fees" question, `q6_est_fee` / `q7_proposed_fee`), or null when
 * the record has no usable figure (uploaded-document QC, blank / N/A answer).
 * Once a QC is approved this figure supersedes the engagement's own Estimated
 * Fees wherever the engagement is displayed.
 *   { amount, currency, scope }
 *   scope 'engagement' — the engagement's own amount from the per-engagement split;
 *   scope 'group'      — only the form's Total, shared by `coveredCount` engagements
 *                        (`coveredNames` lists them);
 *   scope 'single'     — the form covers this engagement alone.
 * Without `engagementId` the Total comes back (scope by `covered`).
 * `covered` = the record's covered engagements (record / slim-list shape).
 */
const qcEstimatedFeeOf = (answers, qcType, { engagementId = null, covered = [] } = {}) => {
  const id = feeItemIdOf(qcType);
  const a = answers || {};
  const ccyRaw = a[`${id}_ccy`];
  const currency = blank(ccyRaw) ? DEFAULT_CURRENCY : String(ccyRaw);
  const list = covered || [];
  if (engagementId !== null && engagementId !== undefined && list.length > 1) {
    const own = splitOf(a, `${id}_split`)[String(engagementId)];
    const n = blank(own) || isAutoValue(own) ? NaN : Number(own);
    if (Number.isFinite(n)) return { amount: n, currency, scope: 'engagement' };
  }
  const v = a[id];
  if (blank(v) || isAutoValue(v)) return null;
  const n = Number(v);
  if (!Number.isFinite(n)) return null;
  if (list.length > 1) {
    return {
      amount: n, currency, scope: 'group', coveredCount: list.length,
      coveredNames: list.map((e) => (e.engagementName
        ? engagementDisplayName(e.engagementName, e.serviceDescription)
        : (e.entityName || `Engagement #${coveredEngagementIdOf(e)}`))),
    };
  }
  return { amount: n, currency, scope: 'single' };
};

/**
 * Keep a form's per-engagement splits in step with its coverage before it is
 * stored: entries for engagements the form no longer covers and blank values
 * drop; a single-engagement form carries no split at all. The whole split
 * drops while the item is Not applicable.
 * `complete` (every write except the data-import service account): splits
 * are all or none — some but not every covered engagement is an error — and
 * the item's own value becomes what they decide (splitDerivedValue).
 * Choice answers must be one of the item's options (a follower's N/A value
 * counts); a split select's "Others" needs its per-engagement specification.
 * Returns `{ answers, error }` — `error` names the first problem (the caller
 * answers 400).
 */
/** Is `v` an acceptable per-engagement value for this split item? */
const splitValueOk = (item, v) => {
  if (item.type === 'money') return Number.isFinite(Number(v)) && Number(v) >= 0;
  if (item.type === 'date') return /^\d{4}-\d{2}-\d{2}$/.test(v);
  if (item.type === 'yesno') return v === 'Yes' || v === 'No';
  if (item.type === 'radio' || item.type === 'select') {
    return (item.options || []).includes(v) || (!!item.splitFollows && v === item.splitFollows.value);
  }
  return true;
};
const normalizeSplits = (answers, qcType, engagementIds = [], { complete = false, requireMoney = false } = {}) => {
  const a = Object.assign({}, answers || {});
  const ids = new Set((engagementIds || []).map((x) => String(x)));
  let error = null;
  getQcItems(qcType).filter((i) => i.split).forEach((item) => {
    const key = splitIdOf(item);
    // `requireMoney` (a new form, or a fee that this write changes): two or
    // more engagements need every engagement's own amount — a Total alone is
    // refused (owner 2026-10-14). A form without any fee yet is the full
    // form's own gap (qcAnswerGaps), not this one's.
    if (requireMoney && item.type === 'money' && ids.size > 1 && !isAutoValue(a[item.id])) {
      const raw = a[key];
      const some = raw && typeof raw === 'object' && !Array.isArray(raw) && Object.values(raw).some((v) => !blank(v));
      const every = some && [...ids].every((id) => !blank(raw[id]));
      if ((some || !blank(a[item.id])) && !every && !error) error = `${item.label} — enter the amount for every engagement`;
    }
    if (!(key in a)) return;
    const raw = a[key];
    delete a[key];
    if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return;
    const otherKey = item.other ? otherSplitIdOf(item) : null;
    const rawSpecs = otherKey ? splitOf(a, otherKey) : {};
    if (otherKey) delete a[otherKey];
    if (ids.size < 2) {
      // Down to one engagement: no split; the value it had becomes the form's.
      const only = [...ids][0];
      const v = only !== undefined ? raw[only] : undefined;
      if (complete && !blank(v) && !isAutoValue(a[item.id]) && splitValueOk(item, String(v).trim())) {
        a[item.id] = String(v).trim();
        if (otherKey && !blank(rawSpecs[only])) a[otherIdOf(item)] = String(rawSpecs[only]).trim();
      }
      return;
    }
    const kept = {};
    if (isChoiceSplit(item)) {
      // A follower (Type of PIE) stays split while the form's answer is N/A
      // only through its own N/A values; any other N/A item drops its split.
      if (isAutoValue(a[item.id]) && !item.splitFollows) return;
      Object.entries(raw).forEach(([eid, v]) => {
        if (!ids.has(String(eid)) || blank(v)) return;
        const t = String(v).trim();
        if (!splitValueOk(item, t)) {
          if (!error) error = `${item.label} — per engagement: "${t}" is not one of the choices`;
          return;
        }
        kept[String(eid)] = t;
      });
      if (Object.keys(kept).length === 0) return;
      a[key] = kept;
      if (otherKey) {
        const specs = {};
        Object.entries(rawSpecs).forEach(([eid, v]) => {
          if (kept[String(eid)] === item.other.value && !blank(v)) specs[String(eid)] = String(v).trim();
        });
        if (Object.keys(specs).length > 0) a[otherKey] = specs;
      }
      if (complete) {
        if ([...ids].some((id) => kept[id] === undefined)) {
          if (!error) error = `${item.label} — per engagement: answer for every engagement, or use one answer for all`;
          return;
        }
        if (otherKey && [...ids].some((id) => kept[id] === item.other.value && blank((a[otherKey] || {})[id]))) {
          if (!error) error = `${item.other.label} — per engagement: specify it for every engagement that picked ${item.other.value}`;
          return;
        }
        a[item.id] = splitDerivedValue(item, a);
      }
      return;
    }
    if (item.type === 'date') {
      if (isAutoValue(a[item.id])) return;
      Object.entries(raw).forEach(([eid, v]) => {
        if (!ids.has(String(eid)) || blank(v)) return;
        const d = String(v).trim();
        if (!/^\d{4}-\d{2}-\d{2}$/.test(d)) {
          if (!error) error = `${item.label} — per engagement: dates must be YYYY-MM-DD`;
          return;
        }
        kept[String(eid)] = d;
      });
      if (Object.keys(kept).length === 0) return;
      a[key] = kept;
      if (complete) {
        if ([...ids].some((id) => kept[id] === undefined)) {
          if (!error) error = `${item.label} — per engagement: enter a date for every engagement, or use one date for all`;
          return;
        }
        a[item.id] = splitEarliest(kept);
      }
      return;
    }
    Object.entries(raw).forEach(([eid, v]) => {
      if (!ids.has(String(eid)) || blank(v)) return;
      const n = Number(v);
      if (!Number.isFinite(n) || n < 0) {
        if (!error) error = `${item.label} — per engagement: amounts must be non-negative numbers`;
        return;
      }
      kept[String(eid)] = String(v).trim();
    });
    if (Object.keys(kept).length === 0) return;
    if (complete) {
      if ([...ids].some((id) => kept[id] === undefined)) {
        if (!error) error = `${item.label} — per engagement: enter an amount for every engagement, or use the Total only`;
        a[key] = kept;
        return;
      }
      a[item.id] = splitSum(kept);
    }
    a[key] = kept;
  });
  return { answers: a, error };
};

/**
 * The form's "Non-audit services (NAS) fee" (`q6_nas_fee` / `q7_nas_fee`):
 * `{ amount, currency }` when answered, `{ none: true }` when the form says
 * there is no NAS, `{ text }` when there is no single figure but the
 * "NAS type(s)" answer carries the form's wording (an imported form that
 * lists several amounts or none), else null (uploaded-document QC, blank
 * answer). Shown under Estimated Fees wherever the engagement is displayed.
 */
const qcNasFeeOf = (answers, qcType) => {
  const p = String(qcType || '').toUpperCase() === 'QC7' ? 'q7' : 'q6';
  const a = answers || {};
  if (no(a[`${p}_has_nas`])) return { none: true };
  const v = a[`${p}_nas_fee`];
  const n = blank(v) || isAutoValue(v) ? NaN : Number(v);
  if (Number.isFinite(n)) {
    const ccy = a[`${p}_nas_fee_ccy`];
    return { amount: n, currency: blank(ccy) ? DEFAULT_CURRENCY : String(ccy) };
  }
  const t = a[`${p}_nas_service_types`];
  return blank(t) || isAutoValue(t) ? null : { text: String(t).trim() };
};


// ── Reviewer plan (who must approve the form) ────────────────────────────────
// The reviewers a request must name follow the form's own answers — the
// engagement type ("This engagement is"), the non-assurance service type and,
// for a QC7, whether the client's risk rating moved from prior year (Client
// risk rated in current year ≠ Risk rating in prior year):
//
//   QC6  audit / review               Partner → Partner
//   QC7  audit / review               Partner (→ Partner when the rating moved)
//   QC6 / QC7  FAS                    Partner → Partner (no condition)
//   QC6 / QC7  routine / non-routine  Director → Partner (→ Partner when the client is a PIE)
//
// The stages are approved in that order; each stage names one person of the
// stated grade. Shared by the forms (which pickers to show) and the server
// (which picks to require) — one rule, one place.
const REVIEWER_STAGE_KEYS = ['engagement', 'additional', 'third'];
const REVIEWER_STAGE_LABELS = ['First Reviewer', 'Second Reviewer', 'Third Reviewer'];
const REVIEWER_STAGE_FIELDS = ['reviewerStaffId', 'additionalReviewerStaffId', 'thirdReviewerStaffId'];
const REVIEWER_STAGE_NAME_FIELDS = ['reviewerName', 'additionalReviewerName', 'thirdReviewerName'];

/** 'audit' | 'nas' | null from the form's "This engagement is" answer. */
const qcServiceTypeOf = (answers, qcType) => {
  const p = String(qcType || '').toUpperCase() === 'QC7' ? 'q7' : 'q6';
  const v = (answers || {})[`${p}_indep_type`];
  if (v === INDEP_TYPE_OPTIONS[0]) return 'audit';
  if (v === INDEP_TYPE_OPTIONS[1]) return 'nas';
  return null;
};

/** 'routine' | 'non_routine' | 'fas' | null from the non-assurance follow-up. */
const qcNasTypeOf = (answers, qcType) => {
  const p = String(qcType || '').toUpperCase() === 'QC7' ? 'q7' : 'q6';
  const v = (answers || {})[`${p}_nas_type`];
  const i = NAS_TYPE_OPTIONS.indexOf(v);
  return i < 0 ? null : ['routine', 'non_routine', 'fas'][i];
};

/**
 * QC7: did the client's risk rating move from prior year? Compares the two
 * answers engagement by engagement when either is split — any engagement
 * whose rating moved is a change — else the form's two answers; null while
 * an answer it needs is blank. A QC6 has no prior year — false.
 */
const qcRiskRatingChangedOf = (answers, qcType) => {
  if (String(qcType || '').toUpperCase() !== 'QC7') return false;
  const a = answers || {};
  const eids = [...new Set([...Object.keys(splitOf(a, 'q7_c_rating_split')), ...Object.keys(splitOf(a, 'q7_prior_risk_split'))])];
  const pairs = eids.length > 0
    ? eids.map((eid) => [qcAnswerForEngagement(a, 'q7_c_rating', eid), qcAnswerForEngagement(a, 'q7_prior_risk', eid)])
    : [[a.q7_c_rating, a.q7_prior_risk]];
  if (pairs.some(([cur, prior]) => RISK_OPTIONS.includes(cur) && RISK_OPTIONS.includes(prior) && cur !== prior)) return true;
  if (pairs.some(([cur, prior]) => !RISK_OPTIONS.includes(cur) || !RISK_OPTIONS.includes(prior))) return null;
  return false;
};

/** true | false | null (unanswered) from the form's "Public Interest Entity (PIE)?" answer. */
const qcPieOf = (answers, qcType) => {
  const p = String(qcType || '').toUpperCase() === 'QC7' ? 'q7' : 'q6';
  const v = (answers || {})[`${p}_pie`];
  if (v === 'Yes') return true;
  if (v === 'No') return false;
  return null;
};

/** Does this route start with a Director (routine / non-routine non-assurance)? */
const isDirectorRoute = (serviceType, nasType) =>
  serviceType === 'nas' && (nasType === 'routine' || nasType === 'non_routine');

/**
 * The stages for a decided set of facts (no answers needed):
 * [{ key, label, grade: 'partner'|'director', field, nameField }].
 * The extra Partner stage: on the Director route (QC6 and QC7) when the
 * client is a PIE; on a QC7 audit / review when the rating moved from prior
 * year. FAS is always two Partners.
 */
const reviewerStagesFor = ({ qcType, serviceType, nasType, riskChanged, pie }) => {
  const directorFirst = isDirectorRoute(serviceType, nasType);
  const isQc7 = String(qcType || '').toUpperCase() === 'QC7';
  const grades = directorFirst
    ? ['director', 'partner', ...(pie ? ['partner'] : [])]
    : (isQc7 && serviceType === 'audit' ? ['partner', ...(riskChanged ? ['partner'] : [])] : ['partner', 'partner']);
  return grades.map((grade, i) => ({
    key: REVIEWER_STAGE_KEYS[i],
    label: REVIEWER_STAGE_LABELS[i],
    grade,
    field: REVIEWER_STAGE_FIELDS[i],
    nameField: REVIEWER_STAGE_NAME_FIELDS[i],
  }));
};

/**
 * The reviewer plan a form's answers call for:
 *   { serviceType, nasType, riskChanged, pie, stages, missing }
 * `stages` is null while the answers cannot decide it; `missing` then names
 * the questions still needed — only the ones the chosen route reads (the
 * QC7 rating change on the Partner route, the PIE answer on the Director
 * route). `riskChanged` may be overridden (the Addendum carries its own
 * "rating revised" answer). `lenient` is for the follow-up flows on a RECORD
 * that never answered the routing questions (a manual record, an import): an
 * unanswered engagement type counts as audit / review, an unanswered
 * non-assurance service type as FAS (both Partner-only, the strictest path),
 * an undecidable QC7 rating change as unchanged and an unanswered PIE as No.
 */
const qcReviewerPlanOf = (answers, qcType, { riskChanged, lenient = false } = {}) => {
  const isQc7 = String(qcType || '').toUpperCase() === 'QC7';
  let serviceType = qcServiceTypeOf(answers, qcType);
  let nasType = serviceType === 'nas' ? qcNasTypeOf(answers, qcType) : null;
  let risk = riskChanged === undefined ? qcRiskRatingChangedOf(answers, qcType) : !!riskChanged;
  let pie = qcPieOf(answers, qcType);
  if (lenient) {
    if (!serviceType) serviceType = 'audit';
    if (serviceType === 'nas' && !nasType) nasType = 'fas';
    if (risk === null) risk = false;
    if (pie === null) pie = false;
  }
  const missing = [];
  if (!serviceType) missing.push('This engagement is');
  if (serviceType === 'nas' && !nasType) missing.push('Type of non-assurance service');
  const directorRoute = isDirectorRoute(serviceType, nasType);
  if (serviceType && !(serviceType === 'nas' && !nasType)) {
    if (directorRoute && pie === null) missing.push('Public Interest Entity (PIE)?');
    if (isQc7 && serviceType === 'audit' && risk === null) missing.push(`${RATING_LABEL} / Risk rating in prior year`);
  }
  if (missing.length > 0) return { serviceType, nasType, riskChanged: risk, pie, stages: null, missing };
  return {
    serviceType, nasType, riskChanged: risk, pie, missing,
    stages: reviewerStagesFor({ qcType, serviceType, nasType, riskChanged: risk, pie }),
  };
};

// ── Legacy import (a signed form uploaded in Management) ─────────────────────
// The questions a record imported from a signed form answers — and nothing
// else, until a QC Amendment completes the form: PIE + Type of PIE and its
// "Others" specification (Particulars; the overdue rules read them), the fee (Estimated Fees display
// + lock), the risk rating (addendum baseline), the EQR (engagement + mail
// groups) and the Expected FS Signing Date (reminder). TNE / TNA are not
// asked: nothing downstream acts on them beyond a read-only display.
const legacyImportItemIds = (qcType) => {
  const p = String(qcType || '').toUpperCase() === 'QC7' ? 'q7' : 'q6';
  return [
    `${p}_pie`, `${p}_pie_type`,
    p === 'q7' ? 'q7_proposed_fee' : 'q6_est_fee',
    `${p}_c_rating`, `${p}_c_rating_reason`, `${p}_c_eqr`, `${p}_c_expected_fs_date`,
  ];
};
// The one legacy question that is NOT mandatory: the rating's reason. The
// rating, its reason and the EQR are addendum-only — a QC Amendment never
// fills them, not even from blank — so an imported record that never had the
// reason on paper gets it here, from the administrator, before the amendment
// completes the rest of the form. Blank stays allowed (historical data).
const LEGACY_OPTIONAL_SUFFIXES = ['_c_rating_reason'];

/**
 * The bank items behind those ids. N/A rules that only look at other legacy
 * questions stay (Type of PIE locks to "not a PIE" when PIE is No); the EQR's
 * rating rule does not apply to a signed form filed outside the system, so it
 * goes and the dropdown offers an explicit "Not required" choice instead.
 */
const legacyImportItems = (qcType) => {
  const byId = new Map(getQcItems(qcType).map((i) => [i.id, i]));
  return legacyImportItemIds(qcType).map((id) => byId.get(id)).filter(Boolean).map((item) => {
    const copy = Object.assign({}, item, { required: !LEGACY_OPTIONAL_SUFFIXES.some((s) => item.id.endsWith(s)) });
    if (item.type === 'eqr') { delete copy.na; copy.allowNotRequired = true; }
    return copy;
  });
};

/** The answer keys a legacy import may carry (money items add `_ccy`). */
const legacyImportFieldIds = (qcType) => legacyImportItems(qcType).flatMap(fieldsOfItem).map((f) => f.id);

/** applyAutoAnswers restricted to the legacy questions (N/A fill, currency default). */
const applyLegacyAutoAnswers = (answers, qcType) => {
  const a = Object.assign({}, answers || {});
  legacyImportItems(qcType).forEach((item) => {
    const st = naState(item, a);
    if (st) {
      a[item.id] = st.value;
      if (item.type === 'money') delete a[`${item.id}_ccy`];
    } else {
      // Only an item with an N/A rule can hold a stale auto value; the EQR's
      // "NOT REQUIRED" is a deliberate choice here, not an auto-fill.
      if (item.na && isAutoValue(a[item.id])) delete a[item.id];
      if (item.type === 'money' && !blank(a[item.id]) && blank(a[`${item.id}_ccy`])) a[`${item.id}_ccy`] = DEFAULT_CURRENCY;
      const followed = followedSplitOf(item, a);
      if (followed !== undefined) {
        if (Object.keys(followed).length === 0) delete a[splitIdOf(item)]; else a[splitIdOf(item)] = followed;
      }
      const derived = splitDerivedValue(item, a);
      if (derived !== null) a[item.id] = derived;
    }
    if (item.other && !otherPicked(item, a)) delete a[otherIdOf(item)];
    const specs = followedOtherSplitOf(item, a);
    if (specs !== undefined) {
      if (Object.keys(specs).length === 0) delete a[otherSplitIdOf(item)]; else a[otherSplitIdOf(item)] = specs;
    }
  });
  return a;
};

/** Ids the legacy form renders locked (their N/A rule applies). */
const legacyLockedFieldIds = (answers, qcType) => {
  const locked = new Set();
  legacyImportItems(qcType).forEach((item) => {
    if (naState(item, answers || {})) fieldsOfItem(item).forEach((f) => locked.add(f.id));
  });
  return locked;
};

/** `answers` reduced to the legacy-import keys, N/A and currency applied. */
// A legacy (signed-form) record must carry the six downstream answers; any
// OTHER question of the same form is kept too when the import could read it
// off the signed PDF (owner 2026-09-24: prior year's fee, activities, NAS,
// unpaid fees, prior risk / opinion / EQR…). Keys that are not questions of
// this form are dropped, so a stray key can never reach the record.
const legacyImportAnswersOf = (answers, qcType) => {
  const allowed = new Set([
    ...legacyImportFieldIds(qcType),
    ...getQcItems(qcType).flatMap(fieldsOfItem).map((f) => f.id),
  ]);
  const out = {};
  Object.entries(answers || {}).forEach(([k, v]) => { if (allowed.has(k)) out[k] = v; });
  return applyLegacyAutoAnswers(out, qcType);
};

/** Legacy-import fields still unanswered — same `[{ key, label }]` shape as qcAnswerGaps. */
const legacyAnswerGaps = (answers, qcType) => {
  const a = answers || {};
  const locked = legacyLockedFieldIds(a, qcType);
  const gaps = [];
  legacyImportItems(qcType).forEach((item) => {
    if (!item.required) return;
    fieldsOfItem(item).forEach((f) => {
      if (f.id.endsWith('_ccy') || f.type === 'split' || locked.has(f.id)) return;
      // A split select's "Others" is specified per engagement (qcSplitGaps).
      if (f.otherOf && (!otherPicked(item, a) || Object.keys(splitOf(a, splitIdOf(item))).length > 0)) return;
      if (blank(a[f.id])) gaps.push({ key: `answers.${f.id}`, label: f.label });
    });
  });
  return gaps;
};
const missingLegacyAnswers = (answers, qcType) => legacyAnswerGaps(answers, qcType).map((g) => g.label);

const QC_TYPE_LABEL = {
  QC6: 'QC6 — Client Acceptance (new client)',
  QC7: 'QC7 — Continuance (existing client)',
};

// CommonJS on purpose: the server renders the approved form to PDF from this
// same bank (server/services/qcFormPdf.js). Webpack resolves the named imports
// the pages use from module.exports.
module.exports = {
  CURRENCIES,
  DEFAULT_CURRENCY,
  RISK_OPTIONS,
  PIE_TYPE_OPTIONS,
  PIE_TYPE_OTHERS,
  fmtItemValue,
  splitLines,
  engagementNameMap,
  splitIdOf,
  otherSplitIdOf,
  isChoiceSplit,
  choiceSplitLines,
  qcAnswerForEngagement,
  feeItemIdOf,
  coveredEngagementIdOf,
  normalizeSplits,
  qcSplitGaps,
  otherIdOf,
  EQR_NOT_REQUIRED,
  INDEP_TYPE_OPTIONS,
  NAS_TYPE_OPTIONS,
  NA,
  getQcForm,
  getQcItems,
  getQcQuestions,
  fieldsOfItem,
  answerableQuestions,
  qcAnswerLabels,
  naState,
  applyAutoAnswers,
  lockedFieldIds,
  isVisible,
  fmtQcAnswerValue,
  fmtMoney,
  expandAnswerChanges,
  qcAnswerGaps,
  missingQcAnswers,
  qcEqrOf,
  addendumOnlyAnswerIds,
  addendumOnlyChanges,
  addendumOnlyHeldIds,
  ADDENDUM_ONLY_NOTE,
  qcEstimatedFeeOf,
  qcNasFeeOf,
  REVIEWER_STAGE_KEYS,
  REVIEWER_STAGE_LABELS,
  REVIEWER_STAGE_FIELDS,
  REVIEWER_STAGE_NAME_FIELDS,
  qcServiceTypeOf,
  qcNasTypeOf,
  qcRiskRatingChangedOf,
  qcPieOf,
  reviewerStagesFor,
  qcReviewerPlanOf,
  legacyImportItemIds,
  legacyImportItems,
  legacyImportFieldIds,
  applyLegacyAutoAnswers,
  legacyLockedFieldIds,
  legacyImportAnswersOf,
  legacyAnswerGaps,
  missingLegacyAnswers,
  QC_TYPE_LABEL,
  qcPrefillGroups,
  feeAnswersChanged,
};
